import { Component, OnInit, AfterViewInit, AfterViewChecked, OnDestroy, Input, ElementRef, HostListener, ViewChild, NgZone, ChangeDetectorRef, ComponentFactoryResolver, ApplicationRef, Injector, EmbeddedViewRef } from '@angular/core';
import { SubmissionExportViewComponent } from '../submission-export-view/submission-export-view.component';
import { EXPORT_STYLES } from 'src/app/generated/export-styles.const';
import { MS_ICON_CSS, replaceMatIconsWithSvg, stripAngularDevAttrs } from 'src/app/utils/ms-icons';
import { PaperReviewListComponent } from '../paper-review-list/paper-review-list.component';
import { SegmentedOption } from '../segmented-control/segmented-control.component';
import { FormGroup, FormBuilder, FormArray, Validators, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { CdkDragDrop, CdkDragMove, moveItemInArray } from '@angular/cdk/drag-drop';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { User } from 'src/app/model/user';
import { Router, ActivatedRoute } from '@angular/router';
import { Mailto, NgxMailtoService } from 'ngx-mailto';
import { TranslateService } from '@ngx-translate/core';
import { Review } from '../../model/paper';
import { Submission, SubmissionStatus } from '../../model/paper';
import { Track } from '../../model/track';
import { ReviewStatus } from 'src/app/enum/review.status';
import { FormFieldType } from 'src/app/enum/form.field.type';
import { FormField } from 'src/app/model/form.field';
import { AuthService } from '../../service/auth.service';
import { InfoService } from '../../service/info.service';
import { SelectOption } from '../../model/select.option';
import { NavbarService } from 'src/app/service/navbar.service';
import { SubmissionsService } from 'src/app/service/submissions.service';
import { FilesService } from '../../service/files.service';
import { EventsService } from 'src/app/service/events.service';
import { NotificationService } from 'src/app/service/notification.service';
import { ReviewsConfigurationService } from 'src/app/service/reviews.configuration.service';
import { EventReviewConfiguration, ViewersWithoutOwnValues } from 'src/app/model/eventReviewConfiguration';
import { ReviewsService } from 'src/app/service/reviews.service';
import { SubmissionWithdrawalComponent } from '../submission-withdrawal/submission-withdrawal.component';
import { SubmissionDeleteComponent } from '../submission-delete/submission-delete.component';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { Event } from 'src/app/model/event';
import { CheckListField, CheckListRole } from 'src/app/model/checklist-field';
import { PaperGroup } from 'src/app/model/paper.group';
import { SubmissionStatusStyles } from 'src/app/enum/submission.status';
import { forkJoin, of, Subscription } from 'rxjs';
import { AdminService } from 'src/app/service/admin.service';
import { ExportService } from 'src/app/service/export.service';
import { CertificatesService } from 'src/app/service/certificates.service';
import { FORCE_LAZY_RENDER_EVENT } from 'src/app/directive/lazy-load.directive';
import { CertificateTemplate, certificateAllowedForStatus } from 'src/app/model/certificate-template';
import { PaperReviewHistoryComponent } from '../paper-review-history/paper-review-history.component';
import { SubmissionHistoryComponent } from '../submission-history/submission-history.component';
import { PrintingProblemComponent } from '../printing-problem/printing-problem.component';
import { PrintingProblemsComponent } from '../printing-problems/printing-problems.component';
import { SubmissionCoverage } from 'src/app/service/publication.service';
import { ChangeTracksDialogComponent } from '../change-tracks-dialog/change-tracks-dialog.component';
import { EventTopic } from 'src/app/model/eventTopic';
import { Author } from 'src/app/model/author';
import { UserEvent } from 'src/app/model/user.event';
import { CountryPipe } from 'src/app/pipe/country.pipe';


export type PaperInfoViewMode = 'full' | 'compact' | 'show-all-submissions';

export const UPDATE_FILE_TYPE = {
  UPLOAD: 'upload',
  DELETE: 'delete'
}

export type TrackFileLoading = {id: number, name: string, loading: boolean, statusUpload: number, onOver: boolean};

export interface ChangeTrackData {
  user_id : number,
  submissionID: number;
  sourceTrack: {id: number, name: string};
  targetTrack: {id: number, name: string};
  tracks: any[];
  oldEvent: number;
  newEvent: number;
  tracksWithFile: string[];
};

@Component({
  selector: 'app-paper-info',
  templateUrl: './paper-info.component.html',
  styleUrls: ['./paper-info.component.scss'],
  preserveWhitespaces: false,
  providers: [CountryPipe],
  // CD default (NÃO OnPush) — decisão consciente. O que travava a página (tooltips lentos,
  // animação picotada, event loop entupido) NÃO era CD em cascata: era um LOOP no setter
  // `@ViewChild abstractLeadRef` que agendava um setTimeout na zona a cada frame → CD →
  // reentrava no setter → ~116×/seg. Corrigido lá (medição única + timer fora da zona).
  // O `authorsResizeObserver` é sensor de EDIÇÃO (some ao sair) e o `window:resize` é raro
  // e não coincide com a animação do menu, então ambos ficam DENTRO da zona (simples).
  // OnPush foi AVALIADO e ADIADO: cascatearia em 8 filhos default-CD que fazem async próprio
  // (chat/upload/reviews) sem `markForCheck` → regressão de render ao vivo. Os
  // `this.cdr.markForCheck()` espalhados já deixam o paper-info "OnPush-ready" p/ um follow-up.
})
export class PaperInfoComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
  @Input() public element: Review | Submission;

  @Input() public viewMode: PaperInfoViewMode = 'full';

  // Nome do evento na linha superior: completo por padrão; cai p/ a sigla
  // (acrônimo + ano) quando não cabe sem invadir o ID / os botões de ação.
  public useEventAcronym = false;

  // True quando a página é exibida na interface do TPC Chair (rota /admin/...).
  // Nesse contexto o título do evento NÃO vira link.
  public isChairInterface = false;

  public form: FormGroup;
  public status: Array<SelectOption>;

  // ── Modo de EDIÇÃO in-place da submissão ──────────────────────────────────
  // O botão de lápis (canto sup. direito) entra/sai deste modo; no modo edição
  // ele fica com fundo accent. Salvar/Descartar ficam na barra flutuante. Fase 1:
  // título + resumo; fases seguintes acrescentam autores/tópicos/formulário custom.
  // Payload PARCIAL ao salvar (PUT partial=True no backend) → cada fase só manda
  // seus campos e o resto é preservado.
  public editMode = false;
  public editForm: FormGroup;
  private editSnapshot = '';

  // Tópicos no modo edição: `editTopics` (objetos) alimenta a lista visível; o
  // controle `topics` do editForm guarda os IDS (serializáveis → entram no dirty
  // e no save). `eventTopicsTree` é a árvore do evento (categorias/subtópicos)
  // buscada ao entrar em edição; `editTopicLeaves` são só as folhas selecionáveis.
  public editTopics: EventTopic[] = [];
  public eventTopicsTree: EventTopic[] = [];
  public editTopicLeaves: EventTopic[] = [];
  // Autores no modo edição: `editAuthors` (objetos) alimenta os pills; o controle
  // `authors` do editForm guarda os IDS na ORDEM atual (dirty + save acompanham
  // remoção e reordenação).
  public editAuthors: Author[] = [];

  // "Resumo." é FIXO dentro do campo editável (imita o resumo não editável): o
  // lead fica absoluto no canto sup. esquerdo e a 1ª linha do textarea recua pela
  // largura DELE (medida aqui → CSS var). Medir cobre qualquer idioma do rótulo.
  public abstractLeadWidth = 0;
  private abstractLeadMeasured = false;
  @ViewChild('abstractLead') set abstractLeadRef(el: ElementRef | undefined) {
    // GUARD (uma vez só): sem isto, o `setTimeout` abaixo disparava CD → o Angular
    // reavaliava este `@ViewChild` → re-entrava aqui → agendava outro `setTimeout`…
    // um loop de ~116×/seg que ENTUPIA o event loop (o `setTimeout(0)` do app chegava
    // a 355ms — matava tooltips e a fluidez geral). O `#abstractLead` é fixo (não pisca),
    // então medir 1× (síncrono + 1 re-medição pós-fontes) basta.
    if (!el || this.abstractLeadMeasured) { return; }
    this.abstractLeadMeasured = true;
    // Seta a CSS var DIRETO no wrapper (o binding `[style.--x.px]` não funciona no
    // Angular 11). Tanto o `<div>` (não editável) quanto o `<textarea>` (edição)
    // herdam a var no `text-indent` → mesma 1ª linha. Largura FRACIONÁRIA
    // (getBoundingClientRect) evita o arredondamento de `offsetWidth`.
    const measure = () => {
      const node = el.nativeElement as HTMLElement;
      const w = node.getBoundingClientRect().width;
      const wrap = node.closest('.abstract-block') as HTMLElement | null;
      if (wrap && w) {
        this.abstractLeadWidth = w;
        wrap.style.setProperty('--abstract-lead-w', w + 'px');
      }
    };
    measure();               // síncrono → sem "flash" de recuo errado no load
    // FORA da zona: só mexe em CSS var/DOM (não precisa de CD) e, crucialmente, não
    // dispara o tick que reentrava no setter. Re-mede após fontes/layout assentarem.
    this.zone.runOutsideAngular(() => setTimeout(measure, 0));
  }

  submissionTrackForm: FormGroup;
  eventTrackList: Array<any>;

  @Input() paperGroupList: Array<SelectOption>;
  @Input() nameFormGroup: string;
  @Input() paperGroupForm: FormGroup;

  public TPCGroupList: Array<SelectOption>;

  // Controle "expandir/recolher todas" na linha do título da seção Revisões.
  // O reviewList vive sob *appLazyLoad → o ViewChild é preenchido quando monta;
  // `reviewsExpanded` espelha o estado default (revisões carregam expandidas).
  @ViewChild(PaperReviewListComponent) reviewList: PaperReviewListComponent;
  public reviewsExpanded = true;
  // Só mostra o botão "expandir/colapsar todos" quando há pelo menos um card
  // expansível. Alimentado pelo output da lista (evita ler o ViewChild no template).
  public reviewsHaveExpandable = false;

  // Modo de visualização das revisões (scores + textos), ligado ao `[layout]` do
  // `app-paper-review-list`. Três estados no toggle, MISTO no meio:
  //   empilhado    → scores e textos um sob o outro;
  //   misto        → scores lado a lado (grade), textos empilhados;
  //   lado-a-lado  → scores e textos lado a lado.
  // Persistido em localStorage (mesmo padrão de `viewProfile`): a escolha vale p/ as
  // PRÓXIMAS submissões. Default = MISTO.
  private static readonly REVIEWS_LAYOUT_KEY = 'jems3-reviews-layout';
  public reviewsLayout: 'stacked' | 'mixed' | 'side-by-side' = PaperInfoComponent.loadReviewsLayout();

  private static loadReviewsLayout(): 'stacked' | 'mixed' | 'side-by-side' {
    let saved: string = null;
    try { saved = localStorage.getItem(PaperInfoComponent.REVIEWS_LAYOUT_KEY); } catch (e) { /* storage indisponível */ }
    return (saved === 'stacked' || saved === 'mixed' || saved === 'side-by-side') ? saved : 'mixed';
  }
  // Ícone do MISTO desenhado à mão (nenhum Material Symbol descreve o conceito):
  // no topo, a MESMA caixa de 3 colunas do lado-a-lado (scores lado a lado);
  // embaixo, UMA barra horizontal (textos empilhados). `currentColor` no stroke →
  // acompanha os estados ativo/hover do segmento.
  private static readonly MIXED_LAYOUT_SVG =
    '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"' +
    ' stroke-width="2" stroke-linejoin="round">' +
    '<rect x="3" y="3.5" width="18" height="9" rx="1.5"/>' +
    '<line x1="9" y1="3.5" x2="9" y2="12.5"/>' +
    '<line x1="15" y1="3.5" x2="15" y2="12.5"/>' +
    '<rect x="3" y="15.5" width="18" height="5" rx="1.5"/>' +
    '</svg>';
  public reviewsLayoutOptions: SegmentedOption[] = [
    { value: 'stacked', label: 'reviews.layout-stacked', icon: 'view_agenda' },
    { value: 'mixed', label: 'reviews.layout-mixed', svg: PaperInfoComponent.MIXED_LAYOUT_SVG },
    { value: 'side-by-side', label: 'reviews.layout-side-by-side', icon: 'view_column' },
  ];
  public setReviewsLayout(mode: 'stacked' | 'mixed' | 'side-by-side'): void {
    this.reviewsLayout = mode;
    // Lembra a escolha p/ as próximas submissões.
    try { localStorage.setItem(PaperInfoComponent.REVIEWS_LAYOUT_KEY, mode); } catch (e) { /* storage indisponível */ }
  }

  @Input() userIsChair: boolean;

  /** Modo prévia read-only — usado pelo `ExportSubmissionsComponent`.
   *  Objetivo: pular ativações que não interessam no preview e que
   *  bombardeiam o backend (fetches per-file do file-rules-card).
   *  NÃO altera visual do fragmento capturado (que é montado antes do
   *  buildPreviewFragment cortar `[remove-from-export]`). */
  @Input() readOnlyPreview = false;
  public userIsSessionChair = false;

  /** Os papéis do usuário SOBRE esta submissão já foram resolvidos?
   *
   *  `userIsChair` chega por HTTP e `userIsSessionChair` nasce `false` e só é
   *  definido em `setPermissions()`. Antes de isso acontecer, TODOS os flags
   *  de papel são falsos — e o `file-rules-card` trata "nenhum papel
   *  restritivo se aplica" como visível (decisão deliberada do projeto).
   *  Resultado: a lista de arquivos aparecia INTEIRA por um instante e só
   *  depois escondia o que o coordenador de sessão / revisor não pode ver
   *  (issue #2381).
   *
   *  Segurar a renderização até aqui resolve sem mexer na política de
   *  visibilidade: o default permissivo continua valendo, mas deixa de ser
   *  consultado com a resposta ainda desconhecida. */
  public permissionsResolved = false;
  public userCanEditStatus: boolean;
  public userCanAssignReviewers = false;
  public showIeeeCopyright = false;
  public send_email: string;
  public similarityVerification: boolean = false;

  // Seletor de PERFIL DE VISUALIZAÇÃO (só p/ TPC chair/admin): permite
  // pré-visualizar a página como autor/revisor/chair. Admin = mesmo perfil do
  // chair (não listado por ora). Default = 'chair' (o perfil real de quem vê).
  // NOTA: por ora só guarda a seleção — aplicar o perfil à visibilidade/ações da
  // página é a próxima fase (incremental).
  // 'publication-coordinator' fica ANTES de 'chair'. Visão = igual à do autor,
  // exceto revisões/rebuttal/discussão (ver effectiveAuthor + effectivePublicationCoordinator).
  public viewProfiles = ['author', 'reviewer', 'session-chair', 'publication-coordinator', 'chair'];
  // Persistido em localStorage: ao trocar a visão, a PRÓXIMA submissão reabre com
  // a última selecionada (só afeta o preview do chair — canPreviewProfile).
  private static readonly VIEW_PROFILE_KEY = 'jems3-paper-view-profile';
  public viewProfile = PaperInfoComponent.loadViewProfile();

  // Acesso via "Publicação > Status de publicação" (query param `?view=publication`):
  // TRAVA a visão no perfil do publication chair (= coordenador de publicações) e
  // ESCONDE o seletor de troca de visão. Independe de canPreviewProfile (os getters
  // effective* curto-circuitam) → funciona mesmo p/ quem não é TPC chair pleno.
  public publicationView = false;

  private static loadViewProfile(): string {
    let saved: string = null;
    try { saved = localStorage.getItem(PaperInfoComponent.VIEW_PROFILE_KEY); } catch (e) { /* storage indisponível */ }
    return ['author', 'reviewer', 'session-chair', 'publication-coordinator', 'chair'].includes(saved) ? saved : 'chair';
  }

  public submissionCovers: SubmissionCoverage[] = [];

  show = false;

  public reviewConfig: EventReviewConfiguration;
  public eventTimeZone: string;

  checklistForm: FormArray;
  checklistFields: Array<CheckListField>;
  checkListRoles: Array<CheckListRole> = [];

  iconCollection: { [key: string]: { fontSet: string, fontIcon: string, color: string } } = {};

  subscriptions: Subscription[] = [];

  SubmissionStatus = SubmissionStatus;

  private updateFileType: string;
  public handlerTrackFiles: Array<TrackFileLoading>;

  public availableTracks: Array<Track>;
  private fontCache: Record<string, string> = {};

  /** Evento tem modelo de certificado salvo? Gate do item "Emitir certificado":
   *  autor só vê com modelo; chair sempre vê, mas desabilitado (com tooltip) sem. */
  public hasCertificateTemplate = false;
  /** Template carregado (p/ avaliar as regras de emissão do chair). */
  private certificateTemplate: CertificateTemplate | null = null;

  /** Status desta submissão permite emitir? (template vazio de status = qualquer). */
  public get certificateStatusAllowed(): boolean {
    return certificateAllowedForStatus(this.certificateTemplate, this.submission && (this.submission.status as any));
  }

  /** Regras de emissão satisfeitas = modelo definido E status permitido. Valem
   *  IGUAL p/ chair e autor (o que difere é o "senão": chair vê desabilitado +
   *  tooltip; autor não vê o item). */
  public get certificateEnabled(): boolean {
    return this.hasCertificateTemplate && this.certificateStatusAllowed;
  }


  constructor(
    private notificationService: NotificationService,
    private submissionsService: SubmissionsService,
    public filesService: FilesService,
    public reviewConfigurationService: ReviewsConfigurationService,
    private reviewsService: ReviewsService,
    private eventsService: EventsService,
    private navbarService: NavbarService,
    public authService: AuthService,
    private infoService: InfoService,
    private adminService: AdminService,
    private exportService: ExportService,
    private fb: FormBuilder,
    private mailtoService: NgxMailtoService,
    private dialog: MatDialog,
    private router: Router,
    private countryPipe: CountryPipe,
    private elementRef: ElementRef,
    private route: ActivatedRoute,
    private certificatesService: CertificatesService,
    private zone: NgZone,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private componentFactoryResolver: ComponentFactoryResolver,
    private appRef: ApplicationRef,
    private injector: Injector,
  ) {
    this.iconCollection = SubmissionStatusStyles;
    this.isChairInterface = this.adminService.isEventAdminRoute(this.route.snapshot);
    // `?view=publication` (link da lista "Status de publicação") trava a visão.
    this.publicationView = this.route.snapshot.queryParamMap.get('view') === 'publication';
  }

  /** "Baixar como página HTML" só habilita quando a página está TOTALMENTE renderizada
   *  (nenhuma seção lazy pendente) — evita exportar HTML incompleto. */
  public pageFullyLoaded = false;
  private lazyLoadPoll?: any;

  ngAfterViewInit(): void {
    // Mede depois do 1º paint e de novo após as ações assíncronas (permissões
    // mudam a largura dos botões); o resize re-mede em runtime. OnPush: marca CD
    // só quando a medida realmente troca sigla↔nome (setTimeout roda na zona → tick).
    const measure = () => { if (this.updateEventLabelFit()) { this.cdr.markForCheck(); } };
    setTimeout(measure, 0);
    setTimeout(measure, 400);
    // Habilita o export "página HTML" só quando não restam placeholders de appLazyLoad.
    setTimeout(() => this.watchPageFullyLoaded(), 0);
    // (resize da janela via @HostListener DENTRO da zona — ver onWindowResize.)
  }

  ngOnDestroy(): void {
    if (this.lazyLoadPoll) { clearInterval(this.lazyLoadPoll); }
    this.authorsResizeObserver?.disconnect();
    document.removeEventListener('keydown', this.onAddAuthorKeydown, true);
  }

  /** Carrega as seções lazy (revisões/discussão/rebuttal) PROATIVAMENTE — sem esperar
   *  o scroll (#2) — e habilita o "Baixar como página HTML" quando tudo montou.
   *
   *  POLLING leve fora da zona (não MutationObserver: observar o subtree inteiro
   *  dispararia a cada mutação → tempestade de CD → travava o navegador). A cada tick:
   *  dispara o force-render (monta seções lazy pendentes) e checa os placeholders. Só
   *  habilita quando NÃO há placeholders de forma ESTÁVEL por alguns ticks — seções que
   *  aparecem só após config assíncrona (rebuttal/discussão após o reviewConfig) não
   *  escapam. `enable` re-entra na zona; atraso final dá tempo do fetch assentar. */
  private watchPageFullyLoaded(): void {
    // Multi-instância (v2 "Ver todas as submissões"): usa o root DESSE
    // componente via elementRef em vez de `getElementById('paperinfo')`,
    // que retornaria só o primeiro `#paperinfo` do DOM e faria todas as
    // outras instâncias esperarem indefinidamente.
    const root = this.elementRef.nativeElement.querySelector('#paperinfo') as HTMLElement | null;
    if (!root) { this.pageFullyLoaded = true; return; }
    const noPending = () => root.querySelectorAll('[data-lazy-load-placeholder]').length === 0;
    const enable = () => this.zone.run(() => {
      this.pageFullyLoaded = true;
      this.cdr.markForCheck();
    });
    // Preview (v2): perfil AGRESSIVO — dados já em cache (bulk pré-fetched),
    // nada de HTTP pra esperar, poll rápido, sem buffer. Cai de ~1.6s pra
    // ~100ms por sub.
    const pollMs = this.readOnlyPreview ? 50 : 300;
    const stableTarget = this.readOnlyPreview ? 1 : 3;
    const postDelay = this.readOnlyPreview ? 0 : 400;
    this.zone.runOutsideAngular(() => {
      let stable = 0;
      this.lazyLoadPoll = setInterval(() => {
        window.dispatchEvent(new CustomEvent(FORCE_LAZY_RENDER_EVENT));   // monta o que está pendente
        if (noPending() && ++stable >= stableTarget) {
          clearInterval(this.lazyLoadPoll);
          this.lazyLoadPoll = undefined;
          // Macrotask (setTimeout 0) em vez de microtask: quebra o mesmo
          // tick de CD, evitando ExpressionChangedAfterItHasBeenCheckedError
          // dev-mode quando o fast-path do preview detecta "pronto" antes de
          // Angular ter estabilizado o primeiro pass sobre pageFullyLoaded.
          setTimeout(enable, postDelay);
        } else if (!noPending()) {
          stable = 0;
        }
      }, pollMs);
    });
  }

  // resize da janela: re-mede o título do evento (sigla↔nome completo) e, em edição,
  // pede o reposicionamento da pílula "+". DENTRO da zona (via @HostListener): o resize
  // de JANELA é raro e NÃO ocorre durante a animação do menu lateral (que redimensiona a
  // área de conteúdo, não a janela), então não precisa da complexidade de rodar fora da
  // zona + rAF-debounce.
  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateEventLabelFit();
    if (this.editMode) { this.repositionAddPill = true; }
  }

  // Sigla do evento (acrônimo + ano) — fallback do nome completo na linha
  // superior quando o espaço é curto.
  public get eventAcronymLabel(): string {
    const ev = this.submission?.event;
    if (!ev) { return ''; }
    return ev.year ? `${ev.acronym} ${ev.year}` : ev.acronym;
  }

  // Decide entre nome completo e sigla medindo a largura natural do nome contra
  // o espaço livre central (linha − 2× o lado mais largo − folga), de modo que o
  // rótulo centralizado não invada o ID nem os botões.
  // Retorna `true` se `useEventAcronym` MUDOU — o chamador (resize debounced, fora da
  // zona) usa isso pra só marcar CD quando o template de fato precisa re-renderizar.
  private updateEventLabelFit(): boolean {
    const host = this.elementRef.nativeElement as HTMLElement;
    const row = host.querySelector('.header-top-row') as HTMLElement;
    const measure = host.querySelector('.event-name-measure') as HTMLElement;
    if (!row || !measure) { return false; }
    const idEl = row.querySelector('.submission-id') as HTMLElement;
    const actionsEl = row.querySelector('._action-buttons') as HTMLElement;
    const idWidth = idEl ? idEl.offsetWidth : 0;
    const actionsWidth = actionsEl ? actionsEl.offsetWidth : 0;
    const sideGap = 24; // folga p/ não encostar nos lados
    const available = row.clientWidth - 2 * Math.max(idWidth, actionsWidth) - 2 * sideGap;
    const fullWidth = measure.offsetWidth;
    const next = fullWidth > available;
    if (next === this.useEventAcronym) { return false; }
    this.useEventAcronym = next;
    return true;
  }

  // Afiliação do autor para exibição: "(SIGLA, País)". Usa a sigla da
  // afiliação (com fallback p/ o nome, via getter do model) + o país do
  // perfil traduzido pelo CountryPipe. Componentes ausentes são pulados;
  // retorna '' quando não há nenhuma informação (o template oculta o span).
  public formatAuthorAffiliation(author: any): string {
    const profile = author?.user?.profile;
    if (!profile) { return ''; }
    // `acronym` é getter do model Affiliation (cai p/ `name` quando vazio), mas
    // só existe se a afiliação for instância de classe (plainToClass). Para
    // payloads que cheguem como objeto plano, repetimos o fallback p/ `name`.
    const acronym = profile.affiliation?.acronym || profile.affiliation?.name;
    const country = profile.country ? this.countryPipe.transform(profile.country) : '';
    const parts = [acronym, country].filter(v => !!v);
    return parts.length ? `(${parts.join(', ')})` : '';
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      status: [this.element.status],
    });

    if (this.isSubmission) {
      this.generateLoadingTrackFile(this.element as Submission);
    }

    const event = this.isReview ? (<Review>this.element).submission.event.id : (<Submission>this.element).event.id;
    this.status = (this.isReview ? ReviewStatus : SubmissionStatus).toSelectable();

    // Preview (v2): pula HTTPs que só alimentam UI ausente no snapshot
    // exportado (certificate template = botão de baixar certificado;
    // publication info = capas/publication controls).
    if (!this.readOnlyPreview) {
      this.setHasCertificateTemplate(event);
      this.submissionsService.getPublicationInfo(this.submissionId).subscribe(covers => {
        this.submissionCovers = covers;
        this.cdr.markForCheck();
      });
    }

    this.reviewConfigurationService.getReviewsConfiguration(event).subscribe(reviewConfig => {
      this.reviewConfig = reviewConfig;
      this.cdr.markForCheck();

      setTimeout(() => {
        this.verifyAndUpdateStatusPending();
        this.setPermissions();

        if (!this.submission.formSubmission) {
          this.getSubmissionAndForm();
        }
        this.cdr.markForCheck();
      });
    }, () => {
      // Papéis do usuário NÃO dependem da config de revisão. Sem este ramo,
      // uma falha aqui deixaria `permissionsResolved` falso para sempre e a
      // seção de arquivos — que passou a depender dele — nunca apareceria.
      // Falhar o carregamento da config não pode esconder os arquivos.
      this.setPermissions();
      this.cdr.markForCheck();
    });
    this.eventTimeZone = this.submission.event.eventInfo.timeZone;
  }

  openInformationDialog(type: string) {
    const id = this.isReview ? (<Review>this.element).id : (<Submission>this.element).id;
    const event = this.isReview ? (<Review>this.element).submission.event.id : (<Submission>this.element).event.id;

    const dialogConfig = new MatDialogConfig();
    dialogConfig.autoFocus = false;
    dialogConfig.width = '55rem';
    dialogConfig.height = '30rem';
    dialogConfig.panelClass = 'my-custom-dialog-class';

    if (type === 'ReviewHistory' || type === 'PrintingProblems') {
      dialogConfig.data = { id, isSubmissionView: this.isSubmission };
    }

    if (type === 'ReviewHistory') {
      this.dialog.open(PaperReviewHistoryComponent, dialogConfig);
    } else if (type === 'SubmissionHistory') {
      dialogConfig.data = { id, userIsChair: this.userIsChair, eventId: event};
      this.dialog.open(SubmissionHistoryComponent, dialogConfig);
    } else if (type === 'PrintingProblems') {
      this.dialog.open(PrintingProblemsComponent, dialogConfig);
    }
  }

  getSubmissionAndForm() {
    this.submissionsService.getSubmission(this.submission.id, true).subscribe(submission => {
      // O endpoint de submissão única (getSubmission) NÃO traz as regras de
      // arquivo da trilha — `track.trackFiles` chega vazio (default do model).
      // Só confiamos no atalho `event instanceof Event` quando a trilha já veio
      // COM as regras; caso contrário buscamos a trilha completa via byTrack
      // (que inclui `trackFiles` e fica em cache desde o submission-view). Sem
      // isso, esta re-busca sobrescrevia a trilha boa por uma com `trackFiles`
      // vazio e os componentes de arquivo sumiam na página (continuavam só no
      // card da lista, que usa getUserSubmissions — esse traz as regras).
      const trackHasFiles = submission.track instanceof Track && submission.track.trackFiles?.length > 0;
      if (submission.event instanceof Event && trackHasFiles) {
        this.generateLoadingTrackFile(submission);
        this.updateSubmission(submission);
      } else {
        const track_id = submission.trackID ?? (submission.track instanceof Track ? submission.track.id : submission.track);

        this.eventsService.getEventByTrack(track_id).subscribe(event_complete => {

          submission.event = event_complete;
          submission.track = event_complete.tracks.find(track => submission.trackID === track.id) || submission.track;
          this.generateLoadingTrackFile(submission);
          this.updateSubmission(submission);
        });
      }
    });
  }

  private generateLoadingTrackFile(submission: Submission): void {
    const trackFiles = (<Track>submission.track).trackFiles;
    this.handlerTrackFiles = trackFiles?.map(track => {
      return {
        id: track.id,
        name: track.name,
        loading: false,
        statusUpload: 0,
        onOver: false
      };
    });
  }

  public verifyAndUpdateStatusPending() {
    if (this.element.status === SubmissionStatus.PENDING && this.filesService.requiredFilesSubmitted((<Track>this.submission.track)?.trackFiles, this.submission.files.map(f => f.trackFile))) {
      this.form.get('status').setValue(SubmissionStatus.ACTIVE);
      this.submitStatusChange();
    }
  }

  public getUpdateFileType($event: string) {
    this.updateFileType = $event;
  }

  public updateSubmission(submission: Submission): void {
    if (!this.isSubmission) {
      (<Review>this.element).submission = submission;
      this.cdr.markForCheck();
      return;
    }

    // O `submission` emitido pelo file-rules-card já é COMPLETO: ele faz o
    // getSubmission (arquivos + status atualizados) e popula event/track ANTES de
    // emitir. Adotamos direto — o antigo poll (getSubmission em setInterval até os
    // arquivos mudarem) era redundante E revertia o status, porque copiava só os
    // arquivos e descartava o status novo (o carimbo, que lê `submission.status`,
    // voltava a Rascunho e exigia refresh).
    this.element = submission;
    if (this.updateFileType) {
      // Veio de upload/remoção de arquivo: zera os indicadores de loading e
      // re-sincroniza o status (o objeto já traz o valor correto do backend).
      this.stopFileLoading();
      if (this.userCanEditStatus) {
        this.form.controls['status'].setValue(this.element.status);
      }
      this.updateFileType = undefined;
    }
    this.cdr.markForCheck();
  }

  /** Zera os indicadores de loading/progresso dos slots de arquivo. */
  private stopFileLoading(): void {
    this.handlerTrackFiles?.forEach(trackFile => {
      trackFile.loading = false;
      trackFile.statusUpload = 0;
    });
    this.cdr.markForCheck();
  }

  public updateSubmissionValue(field: string, value: any) {
    if (this.isSubmission) {
      this.element[field] = value;
    } else {
      (<Review>this.element).submission[field] = value;
    }
    this.cdr.markForCheck();
  }

  public get isReview(): boolean {
    return this.element instanceof Review;
  }

  // Troca o perfil de visualização selecionado (seletor da linha do título).
  // Campos extras com valores vazios/nulos removidos (ex.: opção de checkbox não
  // marcada vira `null`; texto em branco vira ''). Sem isso, valores múltiplos
  // rendiam pílulas VAZIAS. `getSubmissionFormAnswers` mantém os nulos p/ outros
  // consumidores (Lista de Submissões), então filtramos só aqui, na exibição.
  public get extraFieldAnswers() {
    return (this.submission?.getSubmissionFormAnswers() || [])
      // Ordena pela `order` definida pelo chair (a config dos campos extras) — o modelo
      // itera `formSubmission.fields` na ordem do array; ordenar aqui casa com a edição.
      .slice()
      .sort((a, b) => a.order - b.order)
      .map(a => ({
        ...a,
        answers: (a.answers || []).filter(v => v != null && String(v).trim() !== ''),
      }));
  }

  public setViewProfile(profile: string): void {
    // Clicar na visão vigente é no-op — o item não é mais `[disabled]` (pra
    // também realçar no hover), então o clique cai aqui e o menu fecha sozinho.
    if (profile === this.viewProfile) { return; }
    this.viewProfile = profile;
    // Guarda p/ a próxima submissão reabrir na mesma visão.
    try { localStorage.setItem(PaperInfoComponent.VIEW_PROFILE_KEY, profile); } catch (e) { /* storage indisponível */ }
  }

  // Ícone do status da submissão (fontSet/fontIcon) — p/ o pill read-only de
  // status. Mesma fonte de verdade do card de "Minhas submissões".
  public submissionStatusIcon(status: string) {
    return SubmissionStatus.icon(status as SubmissionStatus);
  }

  // Alterna abrir/fechar TODAS as revisões (botão na linha do título da seção).
  // Só lê o ViewChild no clique (nunca no template) → sem ExpressionChanged.
  public toggleAllReviews(): void {
    if (!this.reviewList) { return; }   // lista ainda não montada (lazy)
    this.reviewsExpanded = !this.reviewsExpanded;
    this.reviewList.setAllReviewsExpanded(this.reviewsExpanded);
  }

  // A lista avisa quando (re)computa se algum card é expansível. Defere 1 tick:
  // o emit pode chegar durante a CD do pai (troca de perfil de preview) e mexer
  // no *ngIf do botão causaria ExpressionChangedAfterItHasBeenChecked.
  public onExpandableStateChanged(hasExpandable: boolean): void {
    setTimeout(() => { this.reviewsHaveExpandable = hasExpandable; this.cdr.markForCheck(); });
  }

  public get isSubmission(): boolean {
    return this.element instanceof Submission;
  }

  public get review(): Review {
    return this.isReview ? this.element as Review : undefined;
  }

  public get submission(): Submission {
    return this.isReview ? (this.element as Review).submission : this.element as Submission;
  }

  public get isAuthor(): boolean {
    return this.submission.authors.some(author => author.user.id === this.infoService.user.id);
  }

  // Revisor da submissão: GETTER (não propriedade setada uma vez). Antes era
  // setado em setUserCanAssignReviewers ANTES de `submission.reviews` popular, e
  // não recalculava → o perfil de revisor não era detectado (trilha/subtítulo,
  // botões). Como getter, reflete o estado atual das reviews.
  public get userIsReviewer(): boolean {
    const me = this.infoService.user;
    if (!me) { return false; }
    if (this.isReview) {
      return this.review && this.review.userId === me.id;
    }
    return this.isSubmission && !!this.submission.reviews && this.submission.isReviewer(me.id);
  }

  // ── Perfil de VISUALIZAÇÃO efetivo (single source of truth p/ o template) ────
  // O preview só existe na INTERFACE DO CHAIR e para quem é chair/admin. Na
  // interface do autor/revisor (`isChairInterface === false`), NÃO há seletor e
  // valem os papéis reais — a página é a do autor/revisor. Afeta só a
  // VISIBILIDADE/UI; a lógica/permissões no TS seguem nos flags reais.
  public get canPreviewProfile(): boolean {
    return this.userIsChair && this.isChairInterface;
  }
  public get effectiveChair(): boolean {
    return !this.publicationView && this.canPreviewProfile && this.viewProfile === 'chair';
  }
  // 'publication-coordinator' RENDERIZA como autor (mesma visão) — a diferença é
  // só a supressão de revisões/rebuttal/discussão, feita no template via
  // effectivePublicationCoordinator. Dobrar aqui evita repetir `|| effectivePub…`
  // em cada condição de autor (arquivos, publicação, header, certificado, etc.).
  // `publicationView` (acesso via Status de publicação) força a visão do autor.
  public get effectiveAuthor(): boolean {
    if (this.publicationView) { return true; }
    return this.canPreviewProfile
      ? (this.viewProfile === 'author' || this.viewProfile === 'publication-coordinator')
      : this.isAuthor;
  }
  /** Perfil do publication chair (= coordenador de publicações): visão do autor SEM
   *  revisões/rebuttal/discussão (suprimidas no template). Ativo pelo seletor OU
   *  pelo acesso via "Status de publicação" (publicationView). */
  public get effectivePublicationCoordinator(): boolean {
    return this.publicationView || (this.canPreviewProfile && this.viewProfile === 'publication-coordinator');
  }
  public get effectiveReviewer(): boolean {
    if (this.publicationView) { return false; }
    return this.canPreviewProfile ? this.viewProfile === 'reviewer' : this.userIsReviewer;
  }
  // No preview (chair), vale o perfil SELECIONADO (mutuamente exclusivo); fora
  // do preview, vale o papel real de coordenador de sessão.
  public get effectiveSessionChair(): boolean {
    if (this.publicationView) { return false; }
    return this.canPreviewProfile ? this.viewProfile === 'session-chair' : this.userIsSessionChair;
  }
  // Autor pode trocar a track da própria submissão pra qualquer track ABERTA
  // do evento (ou eventos filho) — regra pré-redesign (`isAuthor && !track.closed`),
  // perdida quando a meta-card (único lugar com o seletor) virou chair-only.
  // Track atual fechada bloqueia a troca pro autor; chair segue trocando pela
  // meta-card sem essa restrição (`eventTrackList` já reflete isso via
  // `!this.userIsChair` em `setEventTracks`).
  public get canAuthorEditTrack(): boolean {
    const track = this.submission?.track;
    const currentTrackClosed = track instanceof Track ? track.closed : false;
    return this.effectiveAuthor && !this.isReview && !!this.submissionTrackForm && !currentTrackClosed && this.editMode;
  }
  // Preview SOMENTE-LEITURA: o chair pré-visualiza um perfil ≠ chair, OU a página
  // foi aberta via Status de publicação. Nesse estado os controles interativos
  // (rebuttal, publicação) ficam desabilitados — vê-se o que o perfil vê, sem AGIR.
  public get previewReadOnly(): boolean {
    return this.publicationView || (this.canPreviewProfile && this.viewProfile !== 'chair');
  }

  // ── Modo de edição in-place ────────────────────────────────────────────────
  // Dirty por snapshot (serializa o form e compara), como no dashboard.
  public get editDirty(): boolean {
    return this.editMode && !!this.editForm
      && JSON.stringify(this.editForm.getRawValue()) !== this.editSnapshot;
  }

  /** Botão de lápis: entra no modo edição; ou, se já editando, tenta sair
   *  (3-way Salvar/Descartar/Cancelar quando há mudanças). */
  public toggleEditMode(): void {
    if (!this.editMode) { this.enterEditMode(); return; }
    if (!this.editDirty) { this.exitEditMode(); return; }
    this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'unsaved-changes-dialog.title',
        content: 'unsaved-changes-dialog.content',
        // [Cancelar (neutral)] [Descartar (destructive)] [Salvar (primary)]
        neutral: 'forms.buttons.cancel', neutralValue: 'cancel',
        confirm: 'forms.buttons.save-changes',
        cancel: 'forms.buttons.discard', cancelValue: 'discard', cancelStyle: 'destructive',
      }
    }).afterClosed().subscribe(result => {
      if (result === true) { this.saveChanges(); }
      else if (result === 'discard') { this.discardChanges(); }
      // 'cancel'/undefined (X/ESC/backdrop) → permanece em edição
    });
  }

  private enterEditMode(): void {
    const topics = this.submission.topics || [];
    const authors = this.submission.authors || [];
    this.editForm = this.fb.group({
      title: [this.submission.title, [Validators.required, Validators.maxLength(150)]],
      abstract: [this.submission.abstract, [Validators.required, Validators.maxLength(3000)]],
      topics: [topics.map(t => t.id), [this.topicsRangeValidator()]],   // IDS (serializáveis) p/ dirty + save
      authors: [authors.map(a => a.user.id)],    // IDS na ORDEM atual (dirty + save)
    });
    this.editTopics = [...topics];
    this.editAuthors = [...authors];   // cópia rasa: remover/reordenar não mexe na submissão até salvar
    this.addAuthorForm = this.fb.group({ user: ['', Validators.required] });
    // Campos extras (formulário custom) → sub-FormGroup 'extraFields' dentro do editForm,
    // pra o dirty-tracking (getRawValue) e o save já os incluírem. Construído A PARTIR de
    // `submission.formAnswer` (as respostas atuais), não de `field.answers` (que fica vazio).
    this.editForm.addControl('extraFields', this.buildExtraFieldsGroup());
    this.editSnapshot = JSON.stringify(this.editForm.getRawValue());
    this.editMode = true;
    this.scheduleAddPillPosition();   // posiciona a pílula "+" após render
    // Árvore de tópicos do evento p/ o painel de seleção (categorias/subtópicos).
    this.eventsService.getEventTopics(this.submission.event.id)
      .subscribe(tree => {
        this.eventTopicsTree = tree || [];
        this.editTopicLeaves = this.flattenTopicLeaves(this.eventTopicsTree);
        this.cdr.markForCheck();
      });
  }

  // ── Campos extras (formulário custom) — edição inline ─────────────────────────
  /** Há campos extras editáveis? (mesma condição do *ngIf da seção na visão.) */
  public get hasExtraFields(): boolean {
    const f = this.submission.formSubmission;
    return !!(f && f.isSubmission() && f.fields && f.fields.length > 0);
  }
  /**
   * Campos a renderizar como editores (ordenados; oculta os `hidden`). Propriedade
   * ESTÁVEL (setada no enterEditMode), não getter — um getter retornaria um array novo
   * a cada CD e o `*ngFor` recriaria os editores (perda de foco ao digitar).
   */
  public editExtraFields: FormField[] = [];
  /** Sub-grupo do editForm que guarda os controles dos campos extras. */
  public get extraFieldsGroup(): FormGroup {
    return this.editForm ? (this.editForm.get('extraFields') as FormGroup) : null;
  }
  /** Enum p/ comparar `field.type` no template sem literais soltos. */
  public FormFieldType = FormFieldType;

  // Editores de ESCOLHA "inline": pílulas clicáveis (reusam `.extra-field-option` da
  // visão) no lugar de mat-radio/mat-checkbox. Manipulam os MESMOS controles do
  // `extraFieldsGroup`, então dirty-tracking e save seguem inalterados.
  public isRadioSelected(fieldId: number, choiceId: number): boolean {
    const c = this.extraFieldsGroup && this.extraFieldsGroup.get(String(fieldId));
    return !!c && String(c.value) === String(choiceId);
  }
  public selectRadio(fieldId: number, choiceId: number): void {
    const c = this.extraFieldsGroup && this.extraFieldsGroup.get(String(fieldId));
    if (c) { c.setValue(String(choiceId)); }
  }
  public isCheckboxSelected(fieldId: number, choiceId: number): boolean {
    const c = this.extraFieldsGroup && this.extraFieldsGroup.get([String(fieldId), String(choiceId)]);
    return !!(c && c.value);
  }
  public toggleCheckbox(fieldId: number, choiceId: number): void {
    const c = this.extraFieldsGroup && this.extraFieldsGroup.get([String(fieldId), String(choiceId)]);
    if (c) { c.setValue(!c.value); }
  }

  /**
   * Monta o FormGroup dos campos extras semeado com as respostas ATUAIS
   * (`submission.formAnswer`). Estrutura casada com `Form.formAnswers(controller)`:
   *   TEXT/RADIO → FormControl (RADIO guarda o id da escolha selecionada);
   *   CHECKBOX   → sub-FormGroup { [choiceId]: boolean }.
   */
  private buildExtraFieldsGroup(): FormGroup {
    this.editExtraFields = [];
    const group = this.fb.group({});
    const form = this.submission.formSubmission;
    if (!form || !form.isSubmission() || !form.fields) { return group; }
    // Editores a renderizar, na ORDEM definida pelo chair (`field.order`). MESMO conjunto
    // da visão (`formSubmission.fields`); cópia (`[...]`) porque `sort` muta o array.
    // A visão (`extraFieldAnswers`) também passa a ordenar por `order`, pra baterem.
    this.editExtraFields = [...form.fields].sort((a, b) => a.order - b.order);
    for (const field of form.fields) {
      // Fonte das respostas atuais p/ o prefill — MESMA lógica da visão
      // (`getSubmissionFormAnswers`): prefere `submission.formAnswer` (populado após um
      // save nosso) e cai pra `field.answers` (onde o GET aninha as respostas — o GET
      // NÃO retorna `form_answer` no topo, o serializer distribui por campo).
      const top = (this.submission.formAnswer || []).filter(a => a.formField === field.id);
      const fAnswers = top.length ? top : (field.answers || []);
      if (field.type === FormFieldType.CHECKBOX) {
        const sub = this.fb.group({});
        for (const c of (field.choices || [])) {
          const ans = fAnswers.find(a => a.fieldChoice === c.id);
          sub.addControl(String(c.id), this.fb.control(!!ans && String(ans.value) === 'true'));
        }
        group.addControl(String(field.id), sub);
      } else if (field.type === FormFieldType.RADIO) {
        // O controle do radio guarda o ID da escolha (casa com `[value]` do botão).
        // Prefere `fieldChoice` (id) e cai pro `value` (que também é o id) por robustez.
        const ans = fAnswers[0];
        const choiceId = ans ? (ans.fieldChoice != null ? ans.fieldChoice : ans.value) : null;
        group.addControl(String(field.id), this.fb.control(choiceId != null ? String(choiceId) : ''));
      } else {                                 // TEXT
        const ans = fAnswers[0];
        const validators = field.required ? [Validators.required] : [];
        group.addControl(String(field.id), this.fb.control(ans != null && ans.value != null ? ans.value : '', validators));
      }
    }
    return group;
  }

  // Painel de TÓPICOS (app-popover ancorado ao campo, backdrop transparente — não
  // escurece, posiciona junto ao gatilho). `topicsSelection` é o conjunto de trabalho
  // enquanto o painel está aberto; ele é COMITADO ao fechar (X/"Fechar"/clique-fora/
  // ESC). O descarte é do modo de edição da página, não daqui.
  public topicsSelection = new Set<number>();

  /** Tópicos SELECIONÁVEIS = folhas: subtópicos de categorias + tópicos standalone. */
  private flattenTopicLeaves(tree: EventTopic[]): EventTopic[] {
    const leaves: EventTopic[] = [];
    tree.forEach(t => {
      if (t.isCategory) { (t.children || []).forEach(c => leaves.push(c)); }
      else { leaves.push(t); }
    });
    return leaves;
  }

  /** app-popover (opened): inicializa o conjunto de trabalho a partir do editForm. */
  public initTopicsSelection(): void {
    this.topicsSelection = new Set<number>((this.editForm.get('topics').value as number[]) || []);
  }

  public isTopicSelected(t: EventTopic): boolean { return this.topicsSelection.has(t.id); }
  public toggleTopic(t: EventTopic): void {
    if (this.topicsSelection.has(t.id)) { this.topicsSelection.delete(t.id); }
    else { this.topicsSelection.add(t.id); }
  }

  public get topicsCount(): number { return this.topicsSelection.size; }
  // 0 / vazio = SEM limite (convenção JEMS3: minTopics/maxTopics 0 não restringe).
  public get topicsMin(): number { const n = Number(this.submission.event.minTopics); return n > 0 ? n : 0; }
  public get topicsMax(): number { const n = Number(this.submission.event.maxTopics); return n > 0 ? n : Infinity; }
  public get topicsHasMax(): boolean { return this.topicsMax !== Infinity; }
  public get topicsValid(): boolean { return this.topicsCount >= this.topicsMin && this.topicsCount <= this.topicsMax; }

  /** Validador do control `topics` do editForm: o mínimo/máximo do evento vale
   *  também na EDIÇÃO, não só na criação (submission-step). Sem isso dava pra
   *  remover tópicos até zero e salvar (issue #2371). Convenção JEMS3: 0/vazio
   *  em minTopics/maxTopics = SEM limite (ver topicsMin/topicsMax acima). */
  private topicsRangeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const n = ((control.value as number[]) || []).length;
      if (n < this.topicsMin) { return { minlength: { requiredLength: this.topicsMin, actualLength: n } }; }
      if (n > this.topicsMax) { return { maxlength: { requiredLength: this.topicsMax, actualLength: n } }; }
      return null;
    };
  }

  /** Mensagem de erro dos tópicos na linha de edição — só depois que o usuário
   *  mexeu (touched) ou tentou salvar, pra não acusar erro de entrada. */
  public get topicsError(): boolean {
    const c = this.editForm?.get('topics');
    return !!c && c.invalid && (c.touched || c.dirty);
  }

  /** app-popover (closed): COMITA o conjunto atual na lista visível e no editForm
   *  (save/dirty acompanham). Sem descarte — isso é função do modo de edição. */
  public commitTopics(): void {
    if (!this.editForm) { return; }
    const chosen = this.editTopicLeaves.filter(t => this.topicsSelection.has(t.id));
    this.editTopics = chosen;
    const control = this.editForm.get('topics');
    control.setValue(chosen.map(t => t.id));
    control.markAsTouched();   // fechou o painel = usuário mexeu → erro pode aparecer
  }

  // ── Autores (modo edição) ────────────────────────────────────────────────────
  /** trackBy dos pills: pelo id do usuário (estável a reordenações). */
  public trackAuthor(_i: number, a: Author): number { return a.user.id; }

  /** Este autor pode ser removido da lista?
   *
   *  Regra de produto: quem CRIOU a submissão não sai da lista de autores —
   *  era assim que um autor transferia a própria submissão para outra pessoa
   *  (issue #2371). Quem impõe é o backend; aqui só evitamos OFERECER um [x]
   *  que resultaria em erro.
   *
   *  Exceção: chair, que administra a autoria. Uso `effectiveChair` (e não
   *  `userIsChair`) de propósito: um chair pré-visualizando como autor deve
   *  ver o que o autor vê.
   *
   *  `creator` nulo = submissão legada sem log de criação; aí não há criador a
   *  proteger e o backend também libera. */
  public canRemoveAuthor(author: Author): boolean {
    if (this.effectiveChair) { return true; }
    const creator = this.submission?.creator;
    return creator == null || author.user.id !== creator;
  }

  /** [x] no pill: remove o autor da lista e sincroniza os ids (dirty/save seguem). */
  public removeAuthor(index: number): void {
    if (!this.editForm) { return; }
    this.editAuthors.splice(index, 1);
    this.syncAuthorsControl();
    this.scheduleAddPillPosition();
  }

  // ── Adicionar autor: pílula "+" FLUTUANTE (posicionada via JS após o último autor,
  //    sem entrar no fluxo → não desloca a lista centralizada). Ao clicar, abre um
  //    overlay CDK ancorado nela com o `app-user-search` (padrão da ferramenta de
  //    e-mail). O escolhido entra em `editAuthors` + sincroniza (dirty/save seguem).
  public addAuthorForm: FormGroup;
  public addAuthorOpen = false;
  public addAuthorAnchor: { elementRef: ElementRef<HTMLElement> } | null = null;
  // Ordem = prioridade. Preferência: abrir abaixo, alinhado à esquerda da pílula.
  // Fallbacks alinham à DIREITA (quando a pílula está perto da borda direita) e/ou
  // abrem PARA CIMA (perto da base). Com `push` ligado no template, o CDK ainda
  // empurra o painel de volta ao viewport se nenhuma posição encaixar por completo.
  public addAuthorPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top',    offsetY: 6 },
    { originX: 'end',   originY: 'bottom', overlayX: 'end',   overlayY: 'top',    offsetY: 6 },
    { originX: 'start', originY: 'top',    overlayX: 'start', overlayY: 'bottom', offsetY: -6 },
    { originX: 'end',   originY: 'top',    overlayX: 'end',   overlayY: 'bottom', offsetY: -6 },
  ];

  public openAddAuthor(anchor: HTMLElement): void {
    this.addAuthorAnchor = { elementRef: new ElementRef(anchor) };
    this.addAuthorOpen = true;
    // ESC fecha TUDO num toque: sem isto, o overlay do autocomplete (mais no topo)
    // captura o ESC via `keydownEvents()`, fecha só o próprio painel ("Criar novo
    // usuário") e faz `stopPropagation` — o popover só fecharia num 2º ESC. Um listener
    // em CAPTURA no document roda ANTES do dispatcher do CDK e fecha o popover inteiro.
    document.addEventListener('keydown', this.onAddAuthorKeydown, true);
  }
  public closeAddAuthor(): void {
    this.addAuthorOpen = false;
    this.addAuthorAnchor = null;
    this.addAuthorForm?.get('user')?.setValue([]);
    document.removeEventListener('keydown', this.onAddAuthorKeydown, true);
    // Chamado tb do listener de keydown (ESC) registrado à mão → fora de um handler de
    // template, precisa marcar CD (nos caminhos de template/HostListener é redundante).
    this.cdr.markForCheck();
    // Mantém o bloco AZUL depois de inserir/cancelar: durante a busca o azul vem da
    // classe `authors-searching`; ao fechar, passa o bastão pro `authorsBorderActive`
    // (`.authors-active`), que persiste até clicar fora do bloco. O mousedown do clique
    // que fechou o popover já ocorreu com `authorsBorderActive=false`, então não desliga.
    this.authorsBorderActive = true;
  }
  /** ESC no popover "+" → fecha o popover inteiro (não só o painel do autocomplete). */
  private onAddAuthorKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && !e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
      e.stopPropagation();   // impede o CDK/autocomplete de tratar (fecharia só o painel)
      e.preventDefault();
      this.closeAddAuthor();
    }
  };
  public onAuthorPicked(picked: User): void {
    this.closeAddAuthor();
    if (!picked || !this.editForm) { return; }
    if (this.editAuthors.some(a => a.user.id === picked.id)) { return; }   // já está na lista
    this.editAuthors.push({ user: picked, order: this.editAuthors.length } as Author);
    this.syncAuthorsControl();
    this.scheduleAddPillPosition();
  }
  public focusAddAuthorInput(): void {
    // O popover é renderizado no CONTAINER DE OVERLAY do CDK (nível do body), FORA do
    // host do componente — então a busca é no `document`, não no elementRef. Além disso,
    // no instante do attach o input pode ainda não existir (o app-user-search monta num
    // setTimeout) OU o foco pode ser roubado logo depois (mat-form-field/autocomplete).
    // Por isso: tenta repetidamente até o input existir E ficar de fato focado.
    let tries = 0;
    const tryFocus = () => {
      const el = document.querySelector('.add-author-popover input') as HTMLInputElement;
      if (el && document.activeElement !== el) { el.focus(); el.select?.(); }
      if (document.activeElement !== el && tries++ < 10) { setTimeout(tryFocus, 20); }
    };
    setTimeout(tryFocus, 0);
  }

  // Flag: pede um reposicionamento da pílula "+" no próximo ngAfterViewChecked (que roda
  // DEPOIS do render — sem a fragilidade de timing do rAF). Setada nos gatilhos (entrar
  // em edição, add/remover autor, fim de arraste, resize).
  private repositionAddPill = false;
  private authorsResizeObserver?: ResizeObserver;
  private scheduleAddPillPosition(): void { this.repositionAddPill = true; }

  ngAfterViewChecked(): void {
    if (!this.repositionAddPill) { return; }
    this.repositionAddPill = false;
    this.positionAddPill();
  }

  private positionAddPill(retries = 10, settlePasses = 6): void {
    if (!this.editMode || this.authorsDragging) { return; }
    const container = this.authorsContainer();
    const pill = container ? (container.querySelector('.author-add-pill') as HTMLElement | null) : null;
    const authors = container ? (Array.from(container.querySelectorAll('.author-editable')) as HTMLElement[]) : [];
    // Referência: o último autor; se não houver, o rótulo "Autores:".
    const ref = pill ? (authors[authors.length - 1] || (container.querySelector('.section-lead') as HTMLElement | null)) : null;
    if (!container || !pill || !ref) {
      // Elementos ainda não renderizados (típico ao REENTRAR no modo edição: a
      // `ngAfterViewChecked` que consumiu a flag rodou antes da pílula existir). Tenta
      // no próximo frame; bounded pra não virar loop se algo impedir a renderização.
      if (retries > 0) { requestAnimationFrame(() => this.positionAddPill(retries - 1, settlePasses)); }
      return;
    }
    // Posiciona por COORDENADAS DE OFFSET do ref (relativas ao offsetParent que a
    // pílula também usa), em vez de `getBoundingClientRect`. O `offsetLeft/Top` já
    // está na mesma base que `pill.style.left/top`, então não há suposição sobre qual
    // é o offsetParent nem sobre bordas do container — era isso que deslocava a pílula
    // na reentrada (a conta com rects assumia container == offsetParent). Pílula e ref
    // compartilham o mesmo offsetParent (ambos filhos do `.authors` position:relative).
    const left = `${Math.round(ref.offsetLeft + ref.offsetWidth + 8)}px`;
    const top = `${Math.round(ref.offsetTop + (ref.offsetHeight - pill.offsetHeight) / 2)}px`;
    // Revela só quando a posição ESTABILIZA (igual à do frame anterior) ou no último
    // passo — evita o flash da pílula aparecendo na posição transitória (área do resumo)
    // enquanto os autores ainda assentam no layout inline/wrap.
    const stable = pill.style.left === left && pill.style.top === top;
    pill.style.left = left;
    pill.style.top = top;
    if (stable || settlePasses === 0) { pill.style.opacity = '1'; }

    // Reposiciona por alguns frames APÓS o 1º cálculo: ao (re)entrar em edição, os
    // autores levam frames pra assumir o layout inline/wrap, e as coordenadas do ref
    // mudam depois. O `ResizeObserver` não pega esse assentamento INTERNO (ele observa
    // o tamanho do CONTAINER, que pode não mudar), então acompanhamos por conta própria
    // até estabilizar. Bounded (settlePasses) → sem loop.
    if (settlePasses > 0) {
      requestAnimationFrame(() => this.positionAddPill(0, settlePasses - 1));
    }

    // Observa o container p/ mudanças EXTERNAS depois de estável (a área de conteúdo muda
    // de largura durante a animação do sidebar, etc.). O pill é absolute → não altera o
    // tamanho do container, sem loop. É um sensor de EDIÇÃO (positionAddPill sai cedo se
    // `!editMode`) e é desligado em exitEditMode → em visualização não existe. Fica DENTRO
    // da zona (simples): só vive durante a edição, e o antigo clogger (loop do
    // abstractLeadRef) já foi corrigido.
    if (!this.authorsResizeObserver && typeof ResizeObserver !== 'undefined') {
      this.authorsResizeObserver = new ResizeObserver(() => this.positionAddPill(0, 0));
      this.authorsResizeObserver.observe(container);
    }
  }

  // Última posição do ponteiro durante o arraste (o CDK 11 não expõe `dropPoint` no
  // evento de drop; `cdkDragMoved.pointerPosition` roda fora da zona → sem CD por move).
  private authorDragPoint = { x: 0, y: 0 };
  // Enquanto true, as vírgulas somem (elas não acompanham o reflow do fantasma).
  public authorsDragging = false;
  // Borda AZUL (accent) do bloco de autores: liga ao COMEÇAR a arrastar e só desliga
  // quando o usuário clica FORA da área (o que inclui interagir com outro campo
  // editável — título/resumo/tópicos ficam fora do bloco). Ver onDocumentMousedown.
  public authorsBorderActive = false;

  @HostListener('document:mousedown', ['$event'])
  public onDocumentMousedown(e: MouseEvent): void {
    const target = e.target as HTMLElement;
    // Cliques DENTRO da busca (caixa do popover ou painel de resultados do autocomplete)
    // são interação normal — não mexem no estado do bloco.
    if (target.closest && (target.closest('.add-author-popover') || target.closest('.mat-autocomplete-panel'))) {
      return;
    }
    // Só há decisão a tomar se a edição de autores está ativa (busca aberta ou azul
    // persistente). Sem isso, saímos ANTES do `clickOutsideAuthorsArea`, que faz um
    // `getBoundingClientRect` = FORCED REFLOW. Ele rodava em TODO mousedown do documento
    // — inclusive ao clicar no toggle do menu lateral —, forçando o reflow (caro) do
    // paper-info bem no início da abertura → a main-thread travava e a animação "pulava".
    if (!this.addAuthorOpen && !this.authorsBorderActive) { return; }
    const outside = this.clickOutsideAuthorsArea(e);
    // Busca ABERTA + clicou fora dela → cancela (fecha aqui no mousedown pra não depender
    // do `backdropClick`, que com o painel do autocomplete aberto nem sempre dispara). Se
    // o clique caiu FORA da área do bloco, também desliga o azul NO MESMO clique (antes
    // exigia um 2º clique); se caiu dentro do bloco, mantém o azul (segue editando).
    if (this.addAuthorOpen) {
      this.closeAddAuthor();                                  // fecha (mantém azul por padrão)
      if (outside) { this.authorsBorderActive = false; }
      return;
    }
    // Busca FECHADA: clique fora do bloco desliga o azul persistente (`authors-active`).
    if (this.authorsBorderActive && outside) {
      this.authorsBorderActive = false;
    }
  }

  /**
   * O clique caiu FORA da área do bloco de autores? Usa `contains` no alvo real (caso
   * sem overlay) e, como fallback, as COORDENADAS do clique — necessário porque, com a
   * busca aberta, o alvo é o backdrop do overlay (cobre a tela toda), e `contains`
   * sozinho diria sempre "fora".
   */
  private clickOutsideAuthorsArea(e: MouseEvent): boolean {
    const authors = this.authorsContainer();
    if (!authors) { return false; }
    const target = e.target as HTMLElement;
    if (target && typeof target.closest === 'function' && authors.contains(target)) { return false; }
    const r = authors.getBoundingClientRect();
    return e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom;
  }
  // Snapshot dos centros dos slots ORIGINAIS (1º move do arraste). O slot de destino é
  // calculado contra ESTE snapshot FIXO — não contra as posições ao vivo. Sem isto, o
  // reflow do fantasma (e a animação) mudaria as medidas, recalculando o slot num loop
  // que oscila e cancela a animação. Com snapshot fixo, `to` é monotônico e estável.
  private authorSlotSnapshot: Array<{ cx: number; cy: number; w: number; h: number }> | null = null;
  // Direção horizontal do arraste (1 = p/ direita, -1 = p/ esquerda) e o último X onde
  // ela foi decidida. A troca dispara na BORDA DE ATAQUE da célula (esquerda indo p/
  // direita, direita indo p/ esquerda) → reage assim que o cursor ENTRA na região do
  // autor, não só no centro. A direção só muda após um movimento mínimo (deadzone),
  // evitando flip por jitter.
  private authorDragDir = 1;
  private authorDragLastX: number | null = null;

  public onAuthorDragStarted(): void {
    this.authorsDragging = true;
    this.authorsBorderActive = true;  // borda azul liga ao começar a arrastar
    this.authorSlotSnapshot = null;   // recaptura no 1º move deste arraste
    this.authorDragDir = 1;
    this.authorDragLastX = null;
  }

  // A cada movimento: atualiza o ponto/direção e move o FANTASMA (placeholder) para o
  // slot de destino → a lista reflui em volta dele, reservando o espaço do autor em
  // trânsito. A reordenação do array só acontece no drop (não brigamos com Angular/CDK).
  public onAuthorDragMoved(event: CdkDragMove): void {
    const px = event.pointerPosition.x;
    if (this.authorDragLastX === null) {
      this.authorDragLastX = px;
    } else {
      const dx = px - this.authorDragLastX;
      if (Math.abs(dx) >= 3) { this.authorDragDir = dx > 0 ? 1 : -1; this.authorDragLastX = px; }
    }
    this.authorDragPoint = event.pointerPosition;
    this.repositionAuthorGhost(event.source.getRootElement(), event.source.getPlaceholderElement());
  }

  /** Insere o fantasma no slot calculado (contra o snapshot fixo), refluindo a lista.
   *  Anima o deslocamento dos DEMAIS pills via FLIP (First-Last-Invert-Play). */
  private repositionAuthorGhost(draggedEl: HTMLElement, ghost: HTMLElement): void {
    const container = this.authorsContainer();
    if (!container || !ghost) { return; }
    const pills = this.visibleAuthorPills(container, draggedEl);
    if (!this.authorSlotSnapshot) {
      // 1º move: captura as posições ORIGINAIS (antes de qualquer reflow do fantasma).
      this.authorSlotSnapshot = pills.map(el => {
        const r = el.getBoundingClientRect();
        return { cx: r.left + r.width / 2, cy: r.top + r.height / 2, w: r.width, h: r.height };
      });
    }
    const to = this.computeAuthorDropIndex();
    const ref = pills[to] || null;   // insere ANTES do pill em `to`, ou no fim
    const willMove = ref ? (ghost.nextElementSibling !== ref) : (container.lastElementChild !== ghost);
    if (!willMove) { return; }

    // FLIP com CSS transition + estilos INLINE `left`/`top` (os pills são
    // position:relative). Inline persiste (o change detection não mexe em estilo
    // inline não-vinculado), é main-thread (verificável) e não depende do objeto de
    // animação da WAAPI sobreviver ao contexto do handler de arraste do CDK — que,
    // por algum motivo, descartava a animação recém-criada. Sem loop de feedback
    // porque o slot vem do SNAPSHOT fixo, não das posições ao vivo.
    const first = pills.map(el => el.getBoundingClientRect());   // posição visual atual
    pills.forEach(el => { el.style.transition = 'none'; el.style.left = ''; el.style.top = ''; });  // neutraliza (interrupção)
    if (ref) { container.insertBefore(ghost, ref); } else { container.appendChild(ghost); }
    const moved = pills.map((el, i) => {
      const last = el.getBoundingClientRect();
      const dx = first[i].left - last.left;
      const dy = first[i].top - last.top;
      if (dx || dy) { el.style.left = `${dx}px`; el.style.top = `${dy}px`; return true; }   // Invert
      return false;
    });
    if (!moved.some(Boolean)) { return; }
    // FLUSH síncrono: commita o offset invertido (senão a transição não tem "de-onde"
    // e o pill pula direto pro lugar).
    void container.getBoundingClientRect();
    // Play: transição + zero → desliza da posição antiga para a nova.
    pills.forEach((el, i) => {
      if (moved[i]) {
        el.style.transition = 'left 160ms cubic-bezier(0, 0, 0.2, 1), top 160ms cubic-bezier(0, 0, 0.2, 1)';
        el.style.left = '0px';
        el.style.top = '0px';
      }
    });
  }

  /** Fim do arraste: limpa a flag e qualquer estilo residual do FLIP (senão os pills
   *  reordenados pelo Angular herdam offsets inline). NÃO reseta o snapshot aqui — o
   *  CDK dispara `ended` ANTES de `dropped`, e o `dropAuthor` ainda precisa do snapshot
   *  p/ calcular o destino. O snapshot é resetado no INÍCIO do próximo arraste. */
  public onAuthorDragEnded(): void {
    this.authorsDragging = false;
    const container = this.authorsContainer();
    if (!container) { return; }
    (Array.from(container.querySelectorAll('.author-editable')) as HTMLElement[]).forEach(el => {
      el.style.transition = '';
      el.style.left = '';
      el.style.top = '';
    });
    this.scheduleAddPillPosition();   // pílula "+" reaparece e reposiciona após o arraste
  }

  /** Arraste de um pill: reordena por ordem de leitura e sincroniza (dirty/save seguem).
   *  O sort do CDK é desligado (quebra em listas com wrap); calculamos o índice nós mesmos. */
  public dropAuthor(event: CdkDragDrop<Author[]>): void {
    if (!this.editForm) { return; }
    // Soltou sem mover (snapshot nunca capturado) → não reordena (evita pulo p/ o 0).
    if (!this.authorSlotSnapshot) { return; }
    // `event.previousIndex` NÃO é confiável aqui (horizontal + wrap → o CDK ordena por
    // X e troca o índice). Pegamos a origem pelo objeto e o destino pelo snapshot.
    const from = this.editAuthors.indexOf(event.item.data as Author);
    if (from < 0) { return; }
    const to = this.computeAuthorDropIndex();
    if (to === from || to < 0) { return; }
    moveItemInArray(this.editAuthors, from, to);
    this.syncAuthorsControl();
  }

  private authorsContainer(): HTMLElement | null {
    return this.elementRef.nativeElement.querySelector('[authors-section] .authors');
  }

  /** Pills visíveis: exclui o arrastado (escondido fora da tela pelo CDK) e o
   *  placeholder/fantasma (que é um CLONE, também `.author-editable`). */
  private visibleAuthorPills(container: HTMLElement, draggedEl: HTMLElement): HTMLElement[] {
    return (Array.from(container.querySelectorAll('.author-editable')) as HTMLElement[])
      .filter(el => el !== draggedEl && !el.classList.contains('cdk-drag-placeholder'));
  }

  /** Índice de destino em ordem de LEITURA (linha acima→abaixo, depois X): conta
   *  quantos slots do SNAPSHOT FIXO ficam ANTES do ponteiro. Estável (sem oscilar).
   *  Na mesma linha, a fronteira é a BORDA DE ATAQUE da célula (não o centro): indo p/
   *  a direita usa a borda ESQUERDA, indo p/ a esquerda usa a DIREITA → a troca dispara
   *  ao ENTRAR na região do autor, e a assimetria de direção dá histerese (sem jitter). */
  private computeAuthorDropIndex(): number {
    const snap = this.authorSlotSnapshot;
    if (!snap) { return 0; }
    const point = this.authorDragPoint;
    let count = 0;
    snap.forEach(s => {
      const rowThreshold = s.h / 2 + 4;
      const dy = s.cy - point.y;
      let before: boolean;
      if (dy < -rowThreshold) { before = true; }        // linha acima
      else if (dy > rowThreshold) { before = false; }   // linha abaixo
      else {
        const edge = this.authorDragDir >= 0 ? s.cx - s.w / 2 : s.cx + s.w / 2;  // borda de ataque
        before = point.x >= edge;
      }
      if (before) { count++; }
    });
    return count;
  }

  /** Reflete a ORDEM/composição atual de `editAuthors` no controle do editForm. */
  private syncAuthorsControl(): void {
    this.editForm.get('authors').setValue(this.editAuthors.map(a => a.user.id));
  }

  private exitEditMode(): void {
    this.editMode = false;
    this.editForm = undefined;
    this.editSnapshot = '';
    this.authorsBorderActive = false;
    // Sensor de EDIÇÃO: o authorsResizeObserver só serve pra reposicionar a pílula "+"
    // (que só existe em edição). Desliga ao sair → não fica "sentindo" resize/animação
    // no modo visualização à toa. É criado sob demanda em positionAddPill (só em edição),
    // então numa sessão de pura visualização ele nem nasce.
    this.authorsResizeObserver?.disconnect();
    this.authorsResizeObserver = undefined;
    // Chamado do subscribe do save (assíncrono) → precisa marcar CD (nos caminhos de
    // template — descartar/cancelar — é redundante, mas inofensivo).
    this.cdr.markForCheck();
  }

  /** Barra flutuante → Salvar. Payload PARCIAL (só os campos desta fase). */
  public saveChanges(): void {
    if (!this.editForm) { return; }
    if (this.editForm.invalid) { this.editForm.markAllAsTouched(); return; }
    const v = this.editForm.getRawValue();
    // O backend (PostSubmissionSerializer.update) espera `authors` como
    // [{ order, user: <id> }] e RECRIA a lista (apaga + insere) quando a chave
    // vem no payload. A ordem do array `editAuthors` já reflete a preferência do
    // usuário (arraste/inserção/remoção), então o índice VIRA o `order` (0-based:
    // autor principal = order 0, coerente com Author.main() no backend).
    const authors = this.editAuthors.map((a, i) => ({ order: i, user: a.user.id }));
    const payload: any = { title: v.title, abstract: v.abstract, topics: v.topics, authors };
    // Campos extras: `Form.formAnswers(controller)` é o método canônico que converte o
    // FormGroup em `Answer[]` ({ formField, value, fieldChoice? }). Vai como `formAnswer`
    // — o CamelCaseJSONParser converte pra `form_answer` e o serializer RECRIA as respostas.
    const form = this.submission.formSubmission;
    let formAnswer: any;
    if (form && form.isSubmission() && this.extraFieldsGroup) {
      formAnswer = form.formAnswers(this.extraFieldsGroup);
      payload.formAnswer = formAnswer;
    }
    this.submissionsService.editSubmission(payload, this.submission.id)
      .subscribe(() => {
        this.submission.title = v.title;
        this.submission.abstract = v.abstract;
        this.submission.topics = this.editTopics;   // a visão passa a refletir os novos tópicos
        // Reflete a nova lista na visão, com `order` recalculado igual ao enviado.
        this.submission.authors = this.editAuthors.map((a, i) => {
          a.order = i;
          return a;
        });
        if (formAnswer) { this.submission.formAnswer = formAnswer; }   // visão reflete os campos extras
        this.exitEditMode();   // silêncio = sucesso: a página já reflete os valores
      });
  }

  /** Barra flutuante → Descartar (ação explícita, sem confirmar). */
  public discardChanges(): void {
    this.exitEditMode();       // form descartado; a exibição volta aos valores atuais
  }

  // ── No-show (tri-estado em `submission.noShow`) ──────────────────────────────
  public get submissionId(): number {
    return this.isReview ? (<Review>this.element).submission.id : (<Submission>this.element).id;
  }

  public getPaperGroup(): string {
    const paperGroup : number | PaperGroup | null = this.submission.paperGroup;
    if(paperGroup && this.paperGroupList){
      if(typeof paperGroup === "number"){
        for(let paperGroupOption of this.paperGroupList){
          if(paperGroupOption.id == paperGroup){
            return paperGroupOption.value;
          }
        }
      } else {
        for(let paperGroupOption of this.paperGroupList){
          if(paperGroupOption.id == paperGroup.id){
            return paperGroupOption.value;
          }
        }
      }
    }
    return "";
  }

  private getTPCGroups(): void {
    const details = "minimum";
    this.eventsService.getEventTPCGroups(this.submission.event.id, details)
      .subscribe(TPCgroups => {
        this.TPCGroupList = TPCgroups.map(TPCgroup => new SelectOption(TPCgroup.id, TPCgroup.name));
        this.cdr.markForCheck();
      })
  }

  public sendEmail(): void {
    let submission = this.submission;
    var usersEmails = this.submission.authors.map(author => {
        let affiliation = ''
        if (author.user.profile.affiliation?.acronym) {
          affiliation = `(${author.user.profile.affiliation.acronym}) `
        }
        return `${author.user.name} ${affiliation}<${author.user.email}>`;
    });

    var recepient = usersEmails[0];
    var cc = usersEmails.slice(1).toString();
    var event = this.submission.event;
    var event_acronym = `${ this.submission.event.year ? this.submission.event.acronym + " " +  this.submission.event.year.toString() : this.submission.event.acronym }`;
    var body = `Dear ${this.submission.authorNames},\n\nNotification for your paper #${submission.id} (${submission.title}) for ${event.name} (${event.acronym} ${event.year}).\n\n`;

    var mailto: Mailto = {
      receiver: recepient,
      cc: cc,
      subject: `[${event_acronym}] Notification for your paper #${submission.id} (${submission.title})`,
      body: body
    };

    this.mailtoService.open(mailto);
  }

  /** Carrega se o evento tem modelo de certificado (gate do item de menu).
   *  Erro → assume "sem modelo" (item some p/ autor, desabilitado p/ chair). */
  private setHasCertificateTemplate(eventId: number): void {
    this.certificatesService.getEventCertificate(eventId).subscribe(
      data => {
        this.certificateTemplate = (data && data.template) || null;
        this.hasCertificateTemplate = !!this.certificateTemplate;
        this.cdr.markForCheck();
      },
      () => { this.certificateTemplate = null; this.hasCertificateTemplate = false; this.cdr.markForCheck(); });
  }

  private setPermissions(): void {
    // ANTES dos ramos: `permissionsResolved` sinaliza que os papéis estão
    // decididos, e o de coordenador de sessão precisa já estar definido nesse
    // momento — senão haveria uma janela com "resolvido" e o flag ainda falso.
    if (this.submission.isSessionChair(this.infoService.user.id)){
      this.userIsSessionChair = true;
    }

    if (this.userIsChair === undefined){
      this.authService.userIsAdminOrChair(this.isReview ?
        (<Review>this.element).submission.event.id :
        (<Submission>this.element).event.id
      ).subscribe(isAdminOrChair => {
        // Poderes de chair valem SÓ na interface do chair (rota admin). Na interface
        // do autor/revisor, mesmo sendo chair do evento, a página segue as regras do
        // papel real (autor/revisor) — o chair não sobrescreve. Sem esse `&&`, um
        // chair que também é revisor da submissão veria a página como chair.
        this.userIsChair = isAdminOrChair && this.isChairInterface;
        this.setUserCanAssignReviewers();
        this.setUserCanEditStatus();
        this.setEventTracks();
        this.setShowIeeeCopyright();
        this.setSimilarityVerification();

        if (!this.paperGroupForm) {
          this.setPaperGroup();
        }

        // this.getCheckListAnswers();
        this.permissionsResolved = true;
        this.cdr.markForCheck();
      });
    } else {
      this.setUserCanAssignReviewers();
      this.setUserCanEditStatus();
      this.setEventTracks();
      this.setShowIeeeCopyright();
      this.setSimilarityVerification();

      if (!this.paperGroupForm) {
        this.setPaperGroup();
      }
      this.permissionsResolved = true;
    }
  }

  public setShowIeeeCopyright(): void {
    if (this.isSubmission && this.submission.status === SubmissionStatus.ACCEPTED && this.submission.event.ieeeCopyrightEnable && (this.userIsChair || this.isAuthor)) {
      this.showIeeeCopyright = true;
    } else {
      this.showIeeeCopyright = false;
    }
  }

  public setUserCanAssignReviewers(): void {
    this.userCanAssignReviewers = this.userIsChair;
  }

  public setSimilarityVerification(): void {
    this.similarityVerification = this.reviewConfig.similarityVerification;
  }

  public setUserCanEditStatus(): void {
    this.userCanEditStatus = this.userIsChair;
  }

  public setPaperGroup(): void {
    if (this.viewMode == 'full' && !this.userIsChair) {
      return;
    }
    // Preview (v2): pula grupos/TPC — selects de edição não aparecem no snapshot.
    if (this.readOnlyPreview) { return; }

    if (!this.isReview) {
      this.getTPCGroups();
      this.eventsService.getEventPaperGroups(this.submission.event.id).subscribe(groups => {
        this.paperGroupList = groups.map(group => new SelectOption(group.id, group.name));

        groups.some(group => {
          const submission = group.submissions.find(s => s.id === this.submissionId);

          if (submission) {
            this.submission.paperGroup = group.id;
          }

          return !!submission;
        });

        this.paperGroupForm = this.fb.group({
          paperGroup: [{ value: this.submission.paperGroup, disabled: this.paperGroupList.length === 0 }],
          tpcGroup: [this.submission.tpcGroup]
        });
        this.cdr.markForCheck();
      });
    }
  }

  public setEventTracks() {
    // This function will fetch all the tracks of the current event.
    // Plus, it will:
    // Consider  tracks of subevents
    // If the user is chair of the parent event, consider tracks of parent AND BROTHER events
    // Plus, the flag track.closed will tell if the option is disabled for selecting
    // Preview (v2): pula — select de trilha não aparece no snapshot.
    if (this.readOnlyPreview) { return; }
    if (!this.isReview && this.viewMode == 'full') {
      // Get parent event id (if there is a parent)
      let parentId: number;
      if(this.submission.event.eventParent instanceof Event){
        parentId = (this.submission.event.eventParent as Event).id
      }else{
        parentId = this.submission.event.eventParent;
      }

      forkJoin(
        [parentId ? this.authService.userIsAdminOrChair(parentId) : of(false), // Only check if user is chair of parent event if it exists
        this.eventsService.getAllSubEventsTracks(this.submission.event?.id),
        parentId ? this.eventsService.getEvent(parentId) : of(null), // Only fetch parent if there is
        parentId ? this.eventsService.getAllSubEventsTracks(parentId) : of(null) // Only fetch parent subevents tracks if there is
        ]
      ).subscribe(([isChairOrAdminParent, subEventsTracks, parentEvent, parentSubEventsTracks]: [boolean, Track[], Event, Track[]])=> {
        this.availableTracks = [...this.submission.event.tracks, ...subEventsTracks];
        
        // If the current logged user is one of the chairs of the parent event (or admin)
        if (isChairOrAdminParent && parentEvent) {
          // Consider parent event's tracks on the list
          const parentTracks = parentEvent.tracks.map(track=> {
            track.name=parentEvent.acronym + ' - '+ track.name;
            return track;
          })

          const brotherTracks = parentSubEventsTracks.filter(track => !this.availableTracks.map(t => t.id).includes(track.id)) // Excludes the tracks already added from the current event
            
          this.availableTracks = [...this.availableTracks, ...parentTracks, ...brotherTracks];
        }
      }, () => {},
      () => {
        // Construct select input based on array of tracks
        this.eventTrackList = this.availableTracks.map(track => new SelectOption(
          track.id, track.name, undefined, undefined,
          (track.closed && !this.userIsChair)
        ));
        this.submissionTrackForm = this.fb.group({
          track: [{ value: this.submission.trackID, disabled: this.eventTrackList?.length === 1 }]
        });
        this.cdr.markForCheck();
      });
    }
  }

  submitStatusChange() {
    if (this.form.valid) {
      const { status } = this.form.value;

      if (this.element instanceof Submission) {
        if (status === SubmissionStatus.WITHDRAWN) {
          //this.openWithdrawal();
        } else {
          this.navbarService.loadingStart();

          this.submissionsService.editSubmission({ status }, this.element.id).subscribe(updatedSubmission => {
            // keys only 'this.element' has:
            //    "noShow", "submittedAt", "statusChangedAt", "printingProblems", "files", "form", "event", "reviews"
            // keys only 'updatedSubmission'
            //    has: "paperGroup", "formAnswer"
            // this is necessary to trigger changes, that only notice if the references change.

            this.refreshSubmission(updatedSubmission);
            this.notificationService.notify('submissions.edit.status', { params: { id: this.element.id } });
            this.navbarService.loadingStop();
          });
        }
      }
    }
  }

  submitTrackChange() {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = false;
    dialogConfig.maxWidth = '700px'

    const newTrack = this.availableTracks.find(track => track.id === this.submissionTrackForm.value.track);
    const oldEventId = this.submission.event.id;
    const newEventId = newTrack.event;
    (dialogConfig.data as ChangeTrackData) = {
      user_id: this.infoService.user?.id,
      submissionID: this.submission.id,
      sourceTrack: {
        id: this.submission.trackID,
        name: this.submission.event.acronym + ' ' +  this.submission.event.year + ' - ' + (this.submission.track as Track).name
      },
      targetTrack: {
        id: this.submissionTrackForm.value.track,
        name: newTrack.name
      },
      tracks: this.availableTracks,
      oldEvent: oldEventId,
      newEvent: newEventId,
      tracksWithFile: this.submission.files.map(file => file.trackFile.name),
    };

    const dialogRef = this.dialog.open(ChangeTracksDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(submission => {
      if (submission?.track && (oldEventId !== newEventId)) {
        if(this.adminService.isEventAdminRoute(this.route.snapshot)){
          // If is in admin route, go back to submissions list
          this.router.navigate(['/admin', oldEventId,'submissions','ranking']);
        }else{
          // If is normal submission page, reload
          window.location.reload();
        }
      }
      this.refreshSubmission(submission);
      this.setEventTracks();
    });
  }

  ieeeElectronicCopyrightComponent(): void {
    this.router.navigate(['/submissions/', this.submission.id, 'ieeeElectronicCopyright']);
  }

  reviewsUpdated(reviews: Review[]) {
    if (this.isSubmission) {
      // TODO Bugfix: class-transformer can't handle transforming dayjs dates,
      // so class-transformer fails on creating a new valid Submission instance.
      // When that is fixed, the following comment updates the review list without an API call.

      // const element = (<Submission> this.element);
      // element.reviews = reviews;
      // (<Submission>this.element) = classToClass(element);

      // WORKAROUND: Refresh submission via API
      this.submissionsService.getSubmission(this.element.id, true).subscribe(s => {
        this.eventsService
          .getEventByTrack(s.trackID)
          .subscribe(event => {
            s.event = event;
            s.track = event.tracks.find(track => s.trackID === track.id);

            this.element = s;
            this.cdr.markForCheck();
          });
        });
    } else {
      if (this.userIsChair || this.reviewConfig.tpcCanView === ViewersWithoutOwnValues.ASSIGNER) {
        // WORKAROUND: Refresh review via API, same as above
        this.reviewsService.getReview(this.review.id, true).subscribe(r => {
          this.eventsService
            .getEventByTrack(this.submission.trackID)
            .subscribe(event => {
              r.submission.event = event;
              r.submission.track = event.tracks.find(track => this.submission.trackID === track.id);

              this.element = r;
              this.cdr.markForCheck();
            });
          });
      } else {
        this.router.navigate(['/reviews']);
      }
    }
  }

  private getCheckListAnswers(updateRoles = true): void {
    this.eventsService.getSubmissionCheckListAnswers(this.submission.id).subscribe(fields => {
      this.checklistFields = fields;

      if (updateRoles) {
        // If checklist defined, obtain user checklist roles
        if (this.checklistFields?.length > 0) {
          if (this.userIsChair) {
            this.checkListRoles.push(CheckListRole.chair);
          }
          if (this.element instanceof Submission) {
            if (this.submission.isAuthor(this.infoService.user.id)) {
              this.checkListRoles.push(CheckListRole.author);
            }

            if (this.submission.isReviewer(this.infoService.user.id)) {
              this.checkListRoles.push(CheckListRole.reviewer);
            }
          }
        // TODO: Publication Chair
        // TODO: Session Chair
        }
      }

      this.checklistForm = this.fb.array(this.checklistFields.map(field => this.fb.group({
          id: [field.answer?.id],
          value: [{ value: field.answer?.value, disabled: !field.userModifiable(this.checkListRoles) }],
          checklist_field: [field.id],
          description: [field.description]
        })
      ));
      this.cdr.markForCheck();
    });
  }

  setChecklistFieldValue(field) {
    const { id } = field.value;

    const obs = id ?
      this.eventsService.editSubmissionCheckListAnswers([field.value], this.submission.id) :
      this.eventsService.createSubmissionCheckListAnswers([field.value], this.submission.id);

    obs.subscribe(() => {
      this.notificationService.notify('submissions.view.checklist-updated', { params: { id: this.submission.id } });
      this.getCheckListAnswers(false);
    });
  }

  openWithdrawal(): void {
    if(this.userCanEditStatus || this.submission.withdrawable){
      const dialogConfig = new MatDialogConfig();
      dialogConfig.autoFocus = false;
      dialogConfig.disableClose = false;
      dialogConfig.data = { submission: this.submission, submissions: [] }
  
      let dialogRef = this.dialog.open(SubmissionWithdrawalComponent, dialogConfig);
  
      dialogRef.afterClosed().subscribe((withdrawedSubmission?: Submission) => {
        if (withdrawedSubmission) {
          this.updateSubmissionValue('status', withdrawedSubmission.status);
          this.refreshSubmission(withdrawedSubmission);
        }
      }, () => {}, () => {
        if (this.userCanEditStatus && !this.isReview) {
          this.form.controls['status'].setValue(this.submission.status);
        }
        this.cdr.markForCheck();
      });
    }else{
      this.notificationService.notifyError('forms.fields.withdraw-unavailable');
    }
  }

  openDelete(): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.autoFocus = false;
    dialogConfig.disableClose = false;
    dialogConfig.data = { submission: this.submission, submissions: [] }

    let dialogRef = this.dialog.open(SubmissionDeleteComponent, dialogConfig);
  }

  refreshSubmission(extraSource?: Submission) {
    // Copy original submission element into a new variable, to trigger ngOnChanges on child components.
    const submission = new Submission();
    Object.assign(submission, this.submission, extraSource);

    if (this.isSubmission) {
      if (!(submission.event instanceof Event)) {
        submission.event = this.submission.event;
      }

      if (!(submission.track instanceof Track)) {
        submission.track = submission.event.tracks.find(t => t.id === submission.trackID);
      }

      this.element = submission;
      this.generateLoadingTrackFile(this.element as Submission);
      this.setShowIeeeCopyright();
      this.cdr.markForCheck();
    } else {
      this.eventsService
        .getEventByTrack(this.submission.trackID)
        .subscribe(event => {
          submission.event = event;
          submission.track = event.tracks.find(track => this.submission.trackID === track.id);

          (<Review>this.element).submission = submission;
          this.cdr.markForCheck();
        });
    }
  }

  refreshReviewRequested(event: { reviewId: number, delete?: boolean }) {
    if (event.delete) {
      if (this.element instanceof Submission) {
        this.element.reviews.splice(this.element.findReviewIndex(event.reviewId), 1);
        this.refreshSubmission();
      } else {
        // Do nothing, because paper-info Review page will break.
        // this.element = undefined;
      }
    } else {
      this.reviewsService.getReview(event.reviewId, true).subscribe(refreshedReview => {
        if (this.element instanceof Submission) {
          const reviewIndex = this.element.reviews.findIndex(r => {
            if (r instanceof Review) {
              return r.id === event.reviewId;
            }
            return r === event.reviewId;
          });

          this.element.reviews.splice(reviewIndex, 1, refreshedReview);
          this.refreshSubmission();
        } else {
          this.element = refreshedReview;
          this.submission.reviews = [this.element];
          this.refreshSubmission();
        }
      });
    }
  }

  /**
   * "Baixar como página HTML": snapshot SÍNCRONO e FIEL do que JÁ ESTÁ renderizado
   * (o que você está vendo agora). NÃO contata o backend. Clona o `#paperinfo`, remove
   * os controles interativos (`[remove-from-export]`) e INLINA o CSS REAL da aplicação
   * — como o clone mantém os atributos de encapsulamento (_ngcontent/_nghost), os
   * estilos de componente casam e o HTML fica idêntico à tela (inclusive as revisões).
   * Regras do app-shell (navbar/sidebar) não têm elementos correspondentes aqui →
   * inofensivas; um override final neutraliza `html`/`body` e apresenta o #paperinfo
   * como uma folha centralizada. Reflete o papel (chair completo, autor restrito).
   */
  /** Gera o fragmento HTML sanitizado do `#paperinfo` DESTA instância,
   *  aplicando a MESMA cirurgia que o "Baixar como página HTML" faz no clone
   *  antes de escrever o arquivo — remove `[remove-from-export]:not(.status-stamp)`,
   *  expande revisões colapsadas, remove `.meta-card` no chair, injeta o
   *  carimbo de status no chair, substitui ícones (info/mail→SVG inline; resto
   *  some), e remove `.file-type-icon`. Retorna o clone embrulhado em um
   *  stand-in do host (mesmo `_nghost-xxx`) para que as regras de
   *  `:host-context` do componente ainda casem quando o fragmento é injetado
   *  via [innerHTML] em outro componente (v2). CSS e fonts vêm do bundle da
   *  app (mesma origem) — não precisa inlinear. */
  public buildPreviewFragment(): string {
    const root = this.elementRef.nativeElement.querySelector('#paperinfo') as HTMLElement | null;
    if (!root) { return ''; }
    const clone = root.cloneNode(true) as HTMLElement;
    clone.querySelectorAll('[remove-from-export]:not(.status-stamp)').forEach(el => el.remove());
    clone.querySelectorAll('.review-body-collapse').forEach(el => {
      (el as HTMLElement).style.height = 'auto';
      (el as HTMLElement).style.overflow = 'visible';
    });
    if (this.effectiveChair) {
      clone.querySelectorAll('.meta-card').forEach(el => el.remove());
      // Carimbo de status — no chair a fonte era a .meta-card (agora removida).
      // Injeta como faz o download.
      if (this.isSubmission && this.submission?.status) {
        const statusClass = String(this.submission.status).toLowerCase();
        const label = this.translate.instant('status.' + this.submission.status);
        const cloneSheet = clone.querySelector('.paper-sheet') as HTMLElement | null;
        const ngAttr = cloneSheet
          ? Array.from(cloneSheet.attributes).map(a => a.name).find(n => n.startsWith('_ngcontent'))
          : undefined;
        const a = ngAttr ? ` ${ngAttr}=""` : '';
        const stampHtml =
          `<div class="status-stamp ${statusClass}"${a} role="img" aria-label="${label}">` +
          `<span class="status-stamp-text"${a}>${label}</span></div>`;
        if (cloneSheet) { cloneSheet.insertAdjacentHTML('beforeend', stampHtml); }
      }
    }
    const ICON_SVG: { [name: string]: string } = {
      info: '<svg viewBox="0 -960 960 960" style="width:1em;height:1em;fill:currentColor;vertical-align:middle"><path d="M440-280h80v-240h-80v240Zm40-320q17 0 28.5-11.5T520-640q0-17-11.5-28.5T480-680q-17 0-28.5 11.5T440-640q0 17 11.5 28.5T480-600Zm0 520q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z"/></svg>',
      mail: '<svg viewBox="0 -960 960 960" style="width:1em;height:1em;fill:currentColor;vertical-align:middle"><path d="M160-160q-33 0-56.5-23.5T80-240v-480q0-33 23.5-56.5T160-800h640q33 0 56.5 23.5T880-720v480q0 33-23.5 56.5T800-160H160Zm320-280L160-640v400h640v-400L480-440Zm0-80 320-200H160l320 200Z"/></svg>',
    };
    clone.querySelectorAll('mat-icon, .material-symbols-outlined, .material-icons').forEach(el => {
      const name = (el.textContent || '').trim().toLowerCase();
      const inReviews = !!el.closest('[reviews-section]');
      if (!inReviews && ICON_SVG[name]) { el.innerHTML = ICON_SVG[name]; } else { el.remove(); }
    });
    clone.querySelectorAll('.file-type-icon').forEach(el => el.remove());
    // Embrulha num stand-in do host (mesmo `_nghost-xxx`) pra que as regras
    // `:host-context(.dark-theme)` do componente ainda casem. Classe
    // `paper-info-wrap` PERMITE que o paper-info.scss detecte empilhamento
    // (irmão de outro wrapper) pra desligar shadow-extensões que clipam
    // a folha vizinha — sem essa classe, os `.paper-info` internos não
    // seriam irmãos diretos e os seletores `+` não casariam.
    const hostEl = this.elementRef.nativeElement as HTMLElement;
    const hostAttr = Array.from(hostEl.attributes).map(a => a.name).find(n => n.startsWith('_nghost'));
    return hostAttr
      ? `<div class="paper-info-wrap" ${hostAttr}="">${clone.outerHTML}</div>`
      : `<div class="paper-info-wrap">${clone.outerHTML}</div>`;
  }


  /** Nome do arquivo exportado: slug do título + id + extensão. */
  private submissionExportFilename(ext: string): string {
    const base = (this.submission.title || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);
    return `${base || 'submissao'}-${this.submission.id}.${ext}`;
  }

  public async downloadSubmissionHtmlPage(): Promise<void> {
    // Fluxo do wrapper com CSS pré-baked (`EXPORT_STYLES`). Mesma técnica
    // usada pelo export em massa (`ExportSubmissionsComponent`) → fonte
    // única visual. O legado `buildSubmissionHtmlDocument` (clone do DOM vivo)
    // foi aposentado — o wrapper offscreen valida melhor.
    const html = await this.buildSubmissionHtmlDocumentFromWrapper();
    this.exportService.downloadFile(this.submissionExportFilename('html'), html, 'text/html');
  }

  /** Passo 8 da migração: monta o HTML instanciando `<app-submission-export-view>`
   *  DINAMICAMENTE em vez de clonar o `#paperinfo` vivo. Objetivo: desacoplar o
   *  download de paper-info (o wrapper vira reusável em outros contextos, como
   *  a v2 do "Exportar submissões"). Reutiliza a maior parte da pipeline
   *  existente (post-processing de ícones, build de CSS, wrap do doc). */
  private async buildSubmissionHtmlDocumentFromWrapper(): Promise<string> {
    // 1. Chair meta label — "Trilha: X - Grupo: Y - TPC Group: Z" pra virar
    //    subtítulo centralizado no header (substitui a meta-card que só faz
    //    sentido interativa). Só paper-info tem acesso ao `TPCGroupList`.
    let chairMetaLabel = '';
    if (this.effectiveChair) {
      const parts: string[] = [];
      const trackName = (this.submission.track as any)?.name;
      if (trackName) { parts.push(`${this.translate.instant('submissions.view.track')}: ${trackName}`); }
      const paperGroupName = this.getPaperGroup();
      if (paperGroupName) { parts.push(`${this.translate.instant('forms.fields.paper-group')}: ${paperGroupName}`); }
      const tpcGroupRaw: any = this.submission.tpcGroup;
      if (tpcGroupRaw && this.TPCGroupList) {
        const tpcId = typeof tpcGroupRaw === 'number' ? tpcGroupRaw : tpcGroupRaw.id;
        const tpc = this.TPCGroupList.find(g => g.id === tpcId);
        if (tpc) { parts.push(`TPC Group: ${tpc.value}`); }
      }
      chairMetaLabel = parts.join(' - ');
    }

    // 2. Instancia o wrapper via ComponentFactory. Anexa OFFSCREEN pra o
    //    CD rodar (`appRef.attachView` + append no `<body>`).
    const factory = this.componentFactoryResolver.resolveComponentFactory(SubmissionExportViewComponent);
    const componentRef = factory.create(this.injector);
    const inst = componentRef.instance;
    inst.submission = this.submission;
    inst.reviews = (this.submission?.reviews as Review[]) || [];
    inst.reviewConfig = this.reviewConfig;
    inst.eventTimeZone = this.eventTimeZone;
    inst.viewerRole = this.effectiveChair ? 'chair' :
                      (this.effectiveReviewer ? 'reviewer' :
                       (this.effectiveSessionChair ? 'session-chair' : 'author'));
    inst.isChairInterface = this.isChairInterface;
    // Carimbo no export SEMPRE (chair inclusive): no live chair, o status vive
    // dentro da meta-card interativa; no export a meta-card é strippada, então o
    // carimbo assume a representação visual do status pra chair também.
    inst.showStamp = this.isSubmission;
    inst.eventAcronymLabel = this.eventAcronymLabel;
    inst.useEventAcronym = false;   // export sempre nome completo
    inst.chairMetaLabel = chairMetaLabel;
    inst.reviewsViewMode = this.viewMode === 'show-all-submissions' ? 'show-all-submissions' : 'full';
    inst.reviewsLayout = this.reviewsLayout || 'stacked';

    this.appRef.attachView(componentRef.hostView);
    const domElem = (componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;
    // Container offscreen (mesmo padrão do liveStamp): posiciona longe da
    // viewport mas mantém no DOM pra layout/CD rodarem.
    const offscreen = document.createElement('div');
    offscreen.style.position = 'fixed';
    offscreen.style.left = '-100000px';
    offscreen.style.top = '0';
    offscreen.style.width = '1200px';   // largura razoável pra medições
    offscreen.style.pointerEvents = 'none';
    offscreen.setAttribute('aria-hidden', 'true');
    offscreen.appendChild(domElem);
    document.body.appendChild(offscreen);
    componentRef.changeDetectorRef.detectChanges();

    // 3. Aguarda o Angular ESTABILIZAR — sinal determinístico: `NgZone.onStable`
    //    emite quando NÃO HÁ mais microtasks/macrotasks/timers pendentes na zona
    //    (CD terminou, HTTP terminou, timers vencidos). Sem timeout arbitrário.
    //    Safety net de 2s caso algo fique sempre agendando (loop infinito de
    //    setInterval, por exemplo) — nesse caso segue com o que tiver.
    await new Promise<void>((resolve) => {
      let done = false;
      const finish = () => { if (done) return; done = true; sub.unsubscribe(); clearTimeout(safety); resolve(); };
      const sub = this.zone.onStable.subscribe(finish);
      const safety = setTimeout(finish, 2000);
    });
    componentRef.changeDetectorRef.detectChanges();

    // 4. Extrai o clone pra post-processing (mesma trimagem do fluxo antigo).
    const root = domElem.querySelector('#paperinfo') as HTMLElement | null;
    const clone = root ? (root.cloneNode(true) as HTMLElement) : null;
    if (clone) {
      clone.querySelectorAll('[remove-from-export]:not(.status-stamp)').forEach(el => el.remove());
      clone.querySelectorAll('.review-body-collapse').forEach(el => {
        (el as HTMLElement).style.height = 'auto';
        (el as HTMLElement).style.overflow = 'visible';
      });
      // Ícones: mantém os que a fonte Material Symbols Outlined (carregada via
      // Google Fonts no doc final) consegue renderizar por ligadura — visual
      // idêntico ao live. Remove só os que não fazem sentido no export
      // estático (interativos: `edit`, `add`, `close`, `unfold_more`, etc.),
      // e todos os dentro de `[reviews-section]` (histórico).
      const REMOVE_ICON_NAMES = new Set([
        'edit', 'add', 'close', 'more_vert', 'more_horiz', 'unfold_more',
        'unfold_less', 'expand_more', 'expand_less', 'delete', 'download',
        'upload', 'settings', 'menu', 'search', 'filter_alt', 'sort',
        'view_agenda', 'view_column', 'drag_indicator', 'chevron_right',
        'chevron_left', 'keyboard_arrow_down', 'keyboard_arrow_up',
        'arrow_drop_down', 'arrow_upward', 'arrow_downward',
      ]);
      clone.querySelectorAll('mat-icon, .material-symbols-outlined, .material-icons').forEach(el => {
        const name = (el.textContent || '').trim().toLowerCase();
        // Antes: removíamos TODOS os ícones em [reviews-section] — tirava também
        // o `fa-check` do "Revisão concluída" (semântico, informativo). Agora
        // só removemos por NOME (interativos). Ícones semânticos ficam; a fonte
        // Material Symbols do Google renderiza as ligaduras.
        if (REMOVE_ICON_NAMES.has(name)) { el.remove(); }
      });
      clone.querySelectorAll('.file-type-icon').forEach(el => el.remove());

      // Rebatiza o `_ngcontent` do wrapper com o `_ngcontent` do paper-info VIVO.
      // Motivo: os seletores scoped compilados de paper-info.scss usam o token do
      // paper-info (ex.: `.paper-sheet[_ngcontent-paper-info-c22]`). Sem esse
      // token nos elementos do wrapper, as regras não casam — resultado: shell
      // (paper-sheet, section, main-body, extras-padding, section-title, etc.)
      // fica sem estilo. Sub-componentes (submission-header, authors-display…)
      // NÃO precisam disso: eles têm seus próprios `_nghost`/`_ngcontent`,
      // compartilhados entre wrapper e paper-info (Angular atribui por TIPO,
      // não por instância) → suas regras já casam nativamente.
      const livePi = document.getElementById('paperinfo');
      const paperInfoNg = livePi
        ? Array.from(livePi.attributes).map(a => a.name).find(n => n.startsWith('_ngcontent'))
        : null;
      const wrapperNg = Array.from(clone.attributes).map(a => a.name).find(n => n.startsWith('_ngcontent'));
      if (paperInfoNg && wrapperNg && paperInfoNg !== wrapperNg) {
        const rebatize = (el: Element) => {
          if (el.hasAttribute(wrapperNg)) {
            el.removeAttribute(wrapperNg);
            el.setAttribute(paperInfoNg, '');
          }
        };
        rebatize(clone);
        clone.querySelectorAll('*').forEach(rebatize);
      }
    }

    // 5. CSS PRÉ-BAKED (constante gerada por `npm run gen:export-styles`).
    //    Elimina o walk das `document.styleSheets` (custo O(regras × elementos)).
    //    A rebatização de `_ngcontent` do clone (feita acima) é DESNECESSÁRIA
    //    pra este CSS — ele foi compilado sem encapsulation e aplica por classe
    //    pura. Mantemos a rebatização por precaução (aliás inofensiva).
    const appCss = EXPORT_STYLES;

    // 6. Cleanup do wrapper vivo.
    this.appRef.detachView(componentRef.hostView);
    componentRef.destroy();
    offscreen.remove();


    // 7. Wrap no doc final. IMPORTANTE: envolvemos o clone em `<app-paper-info>`
    //    (mesmo tag do componente vivo) pra que os SELETORES DE ANCESTRAL nas
    //    regras globais casem — sem isso, `_color-scheme.scss` (que tem regras
    //    tipo `app-paper-info .paper-info a { color: X }`) não pega e o export
    //    diverge do live sem motivo (link ficaria cinza claro em vez de escuro).
    const themeClass = (document.body.className.match(/\b(?:light|dark)-theme\b/) || ['light-theme'])[0] || 'light-theme';
    const pageBg = themeClass === 'dark-theme' ? '#000' : '#EEECEB';
    const wrapped = clone
      ? `<app-paper-info class="${themeClass}">${stripAngularDevAttrs(replaceMatIconsWithSvg(clone.outerHTML))}</app-paper-info>`
      : '';
    const html = `<!doctype html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${this.submission.title}</title>
<style>${appCss}
${MS_ICON_CSS}</style>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Geist:wght@100..900&family=Inconsolata:wght@200..900&family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200&display=swap" rel="stylesheet">
<style>
  html, body { margin: 0 !important; padding: 0 !important; height: auto !important; min-height: 100vh !important; overflow: visible !important; }
  /* Overscroll (macOS bounce) revela bg do html. Sem esta regra, cai no
     html:has(body.light-theme) do _color-scheme (background:white, spec 0,1,1).
     !important empata specificidade e ordem posterior vence. */
  html { background: ${pageBg} !important; }
  body {
    background: ${pageBg} !important;
    font-family: 'Geist', -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif;
    font-size: 0.875rem;
    line-height: 1.25rem;
    letter-spacing: 0.015625rem;
    font-feature-settings: "kern", "liga", "calt", "tnum";
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
  }
  #paperinfo { background: transparent !important; }
</style>
<script>
  // Medição do .abstract-lead no contexto de renderização FINAL do HTML
  // exportado (não do wrapper offscreen do app). Sem isso, o --abstract-lead-w
  // sai com valor medido em contexto de fonte não-estabilizada (5-10% maior) →
  // "Resumo." e o texto ficam desalinhados. Roda 2×: imediato + após fontes.
  (function() {
    function measure() {
      document.querySelectorAll('.abstract-block').forEach(function(block) {
        var lead = block.querySelector('.abstract-lead');
        if (!lead) return;
        var w = lead.getBoundingClientRect().width;
        if (w) block.style.setProperty('--abstract-lead-w', w + 'px');
      });
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', measure);
    } else {
      measure();
    }
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measure);
    } else {
      window.addEventListener('load', measure);
    }
  })();
</script>
</head>
<body class="${themeClass}">${wrapped}</body>
</html>`;
    return html;
  }

  /** "Baixar como arquivo PDF": MESMO HTML do download HTML, convertido no backend
   *  (/core/htmlToPdf) e baixado como .pdf. Mostra a barra de progresso durante a
   *  conversão (ida ao servidor); toast de erro se falhar. */
  public async downloadSubmissionPdf(): Promise<void> {
    // Remove os <link> de fontes EXTERNAS (Google Fonts) do HTML enviado ao backend:
    // o CreatePdf usa Playwright com `wait_until: networkidle`, e um recurso externo
    // que o servidor não consegue baixar (sem internet) TRAVA a espera → timeout → 500.
    // No PDF a fonte cai no fallback do sistema (ok). O download HTML mantém os links
    // (lá quem baixa a fonte é o navegador do usuário).
    // No PDF a folha da submissao E a propria pagina: sem fundo cinza, sem a folha
    // branca "flutuando" com sombra/margem por cima. Achatamos a .paper-sheet para
    // preencher a pagina (o padding dela vira a margem de conteudo). O fundo do
    // documento acompanha o bg COMPUTADO da folha (branco no claro, escuro no dark).
    const liveSheet = document.getElementById('paperinfo')?.querySelector('.paper-sheet');
    const sheetBg = liveSheet ? getComputedStyle(liveSheet).backgroundColor : '#fff';
    const pdfFlattenCss =
      `<style>` +
      // min-height:0 anula o `min-height:100vh` do export (que serve ao HTML) para a
      // altura medida bater com o conteudo real -> pagina unica sem faixa em branco.
      `html, body { background: ${sheetBg} !important; min-height: 0 !important; }` +
      `.paper-info { background: transparent !important; box-shadow: none !important; padding: 0 !important; min-height: 0 !important; }` +
      `.paper-info .paper-sheet { box-shadow: none !important; max-width: none !important; margin: 0 !important; }` +
      `</style>`;
    // Usa o MESMO fluxo do download HTML (wrapper offscreen + EXPORT_STYLES).
    // Antes usava `buildSubmissionHtmlDocument` (fluxo legacy clone) — removido.
    const baseHtml = await this.buildSubmissionHtmlDocumentFromWrapper();
    const html = baseHtml
      .replace(/<link\b[^>]*fonts\.(?:googleapis|gstatic)\.com[^>]*>/gi, '')
      .replace('</head>', pdfFlattenCss + '</head>');
    this.adminService.progressBar?.start();
    // A4 (~794px) é mais estreito que a folha (até 1100px) → as revisões cairiam
    // para 2 colunas em vez de 4. Larga o bastante pra folha; SEM altura → o backend
    // mede o conteúdo e gera uma ÚNICA página longa (sem cortes em alturas A4).
    const pageSize = { width: '1200px' };
    this.exportService.transformHtmlToPdf(html, pageSize).subscribe(
      pdf => {
        this.exportService.downloadBlob(this.submissionExportFilename('pdf'), pdf);
        this.adminService.progressBar?.stop();
        this.cdr.markForCheck();
      },
      () => {
        this.notificationService.notifyError('forms.buttons.download-submission-pdf-error');
        this.adminService.progressBar?.stop();
        this.cdr.markForCheck();
      }
    );
  }

  /**
   * Enxuga o CSS da aplicação para o export: percorre TODAS as folhas (same-origin) e
   * mantém só as regras cujos seletores casam com algum elemento DENTRO do `root`
   * (#paperinfo vigente). Testa no DOM VIVO (com os ancestrais reais → respeita
   * `.dark-theme .x`, `body .y`, etc.). Pseudos de estado/elemento (`:hover`, `::before`…)
   * são removidos antes do teste (não casariam num snapshot estático). Seletores que o
   * navegador não consegue testar → mantidos (seguro). Corta a maior parte do
   * Material/Bulma não-usado; `@font-face`/`@keyframes`/`@media` preservados.
   */
  private buildExportCss(root: HTMLElement | null): string {
    if (!root) { return ''; }
    const stripPseudos = (sel: string): string => sel
      .replace(/::?(hover|focus(?:-visible|-within)?|active|visited|target|checked|indeterminate|disabled|enabled|read-only|read-write|placeholder-shown|default|valid|invalid|required|optional|before|after|placeholder|selection|first-line|first-letter|marker|backdrop|-webkit-[\w-]+|-moz-[\w-]+)(?:\([^()]*\))?/gi, '')
      .replace(/\s{2,}/g, ' ').trim();
    const used = (selector: string): boolean => {
      const base = stripPseudos(selector);
      if (!base) { return true; }
      try {
        const found = document.querySelectorAll(base);
        for (let i = 0; i < found.length; i++) {
          if (root === found[i] || root.contains(found[i])) { return true; }
        }
        return false;
      } catch (e) { return true; }
    };
    const walk = (rules: CSSRuleList): string[] => {
      const out: string[] = [];
      Array.from(rules).forEach((rule: any) => {
        if (rule.selectorText !== undefined) {                       // CSSStyleRule
          if (String(rule.selectorText).split(',').some((s: string) => used(s))) { out.push(rule.cssText); }
        } else if (rule.cssRules && (rule.media !== undefined || rule.conditionText !== undefined)) { // @media/@supports
          const inner = walk(rule.cssRules);
          if (inner.length) { out.push(rule.cssText.slice(0, rule.cssText.indexOf('{') + 1) + inner.join('\n') + '}'); }
        } else if (rule.cssText) {                                   // @keyframes, @page…
          // Pula @font-face: as fontes são auto-hospedadas com url() RELATIVA →
          // quebram num arquivo standalone (404) e ainda conflitam com o Material
          // Symbols do Google (mesmo nome). Ícones vêm do <link> do Google; texto
          // cai na fonte de sistema (o @font-face quebrado não renderizaria mesmo).
          if (String(rule.cssText).trim().toLowerCase().startsWith('@font-face')) { return; }
          out.push(rule.cssText);
        }
      });
      return out;
    };
    const kept: string[] = [];
    Array.from(document.styleSheets).forEach(sheet => {
      try { kept.push(...walk((sheet as CSSStyleSheet).cssRules)); }
      catch (e) { /* folha cross-origin → pula */ }
    });
    return kept.join('\n');
  }

}
